import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import HeroBuilder from './components/HeroBuilder';

// Use relative API path for production compatibility
// In production, /api/* routes to backend; in dev, REACT_APP_BACKEND_URL provides the full URL
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL && BACKEND_URL.trim() ? `${BACKEND_URL}/api` : '/api';

// ============== AUTH HOOK ==============
const useAdminAuth = () => {
  const [token, setToken] = useState(localStorage.getItem('admin_token'));
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const verifyToken = async () => {
      if (!token) {
        setLoading(false);
        return;
      }
      try {
        await axios.get(`${API}/admin/me`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setIsAuthenticated(true);
      } catch (error) {
        localStorage.removeItem('admin_token');
        setToken(null);
        setIsAuthenticated(false);
      }
      setLoading(false);
    };
    verifyToken();
  }, [token]);

  const login = async (email, password) => {
    const response = await axios.post(`${API}/admin/login`, { email, password });
    localStorage.setItem('admin_token', response.data.token);
    setToken(response.data.token);
    setIsAuthenticated(true);
    return response.data;
  };

  const setup = async (email, password) => {
    const response = await axios.post(`${API}/admin/setup`, { email, password });
    localStorage.setItem('admin_token', response.data.token);
    setToken(response.data.token);
    setIsAuthenticated(true);
    return response.data;
  };

  const logout = () => {
    localStorage.removeItem('admin_token');
    setToken(null);
    setIsAuthenticated(false);
  };

  const getAuthHeaders = useCallback(() => ({
    Authorization: `Bearer ${token}`
  }), [token]);

  return { token, isAuthenticated, loading, login, setup, logout, getAuthHeaders };
};

// ============== ADMIN LOGIN ==============
const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isSetup, setIsSetup] = useState(false);
  const [checkingSetup, setCheckingSetup] = useState(true);

  useEffect(() => {
    const checkSetup = async () => {
      try {
        const response = await axios.get(`${API}/admin/check-setup`);
        setIsSetup(!response.data.admin_exists);
      } catch (error) {
        console.error('Error checking setup:', error);
      }
      setCheckingSetup(false);
    };
    checkSetup();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (isSetup) {
        await onLogin.setup(email, password);
      } else {
        await onLogin.login(email, password);
      }
    } catch (error) {
      setError(error.response?.data?.detail || 'Login failed');
    }
  };

  if (checkingSetup) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-gray-800 rounded-lg shadow-xl p-8">
        <div className="text-center mb-8">
          <h1 className="text-2xl font-bold text-white mb-2">
            {isSetup ? 'Setup Admin Account' : 'Admin Login'}
          </h1>
          <p className="text-gray-400 text-sm">The Timeless Post Content Management</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-gray-300 text-sm mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-amber-500"
              required
            />
          </div>

          <div>
            <label className="block text-gray-300 text-sm mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-3 text-white focus:outline-none focus:border-amber-500"
              required
              minLength={6}
            />
          </div>

          {error && (
            <div className="text-red-400 text-sm bg-red-900/20 border border-red-800 rounded p-3">
              {error}
            </div>
          )}

          <button
            type="submit"
            className="w-full bg-amber-600 hover:bg-amber-500 text-white font-medium py-3 rounded transition-colors"
          >
            {isSetup ? 'Create Admin Account' : 'Sign In'}
          </button>
        </form>
      </div>
    </div>
  );
};

// ============== SIDEBAR ==============
const AdminSidebar = ({ activeTab, setActiveTab, onLogout }) => {
  const tabs = [
    { id: 'dashboard', label: 'Dashboard', icon: '📊' },
    { id: 'homepage', label: 'Homepage Builder', icon: '🏠' },
    { id: 'hero-media', label: 'Hero Media', icon: '🎬' },
    { id: 'custom-pages', label: 'Custom Pages', icon: '📝' },
    { id: 'characters', label: 'Characters', icon: '👤' },
    { id: 'letters', label: 'Scheduled Letters', icon: '✉️' },
    { id: 'products', label: 'Shop Products', icon: '🛍️' },
    { id: 'memberships', label: 'Memberships', icon: '⭐' },
    { id: 'subscriptions', label: 'Subscriptions', icon: '🔄' },
    { id: 'orders', label: 'Orders', icon: '📦' },
    { id: 'customers', label: 'Customers', icon: '👥' },
    { id: 'emails', label: 'Email Automation', icon: '📧' },
    { id: 'media', label: 'Media Library', icon: '🖼️' },
    { id: 'pages', label: 'Policy Pages', icon: '📄' },
    { id: 'site-settings', label: 'Site Settings', icon: '⚙️' },
    { id: 'lead-magnets', label: 'Lead Magnets', icon: '📧' },
  ];

  return (
    <div className="w-64 bg-gray-800 min-h-screen p-4 flex flex-col overflow-y-auto">
      <div className="mb-6">
        <h1 className="text-xl font-bold text-white">The Timeless Post</h1>
        <p className="text-gray-400 text-sm">Admin Dashboard</p>
      </div>

      <nav className="flex-1 space-y-1">
        {tabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`w-full text-left px-3 py-2 rounded-lg flex items-center gap-3 transition-colors text-sm ${
              activeTab === tab.id
                ? 'bg-amber-600 text-white'
                : 'text-gray-300 hover:bg-gray-700'
            }`}
          >
            <span>{tab.icon}</span>
            <span>{tab.label}</span>
          </button>
        ))}
      </nav>

      <div className="pt-4 border-t border-gray-700 mt-4">
        <a href="/" className="block text-gray-400 hover:text-white px-3 py-2 text-sm">
          ← Back to Site
        </a>
        <button onClick={onLogout} className="w-full text-left px-3 py-2 text-red-400 hover:text-red-300 text-sm">
          Sign Out
        </button>
      </div>
    </div>
  );
};

// ============== FILE UPLOAD ==============
const FileUpload = ({ uploadType, onUpload, currentUrl, label, accept, compact = false }) => {
  const [uploading, setUploading] = useState(false);
  const token = localStorage.getItem('admin_token');

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(
        `${API}/admin/upload/${uploadType}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      onUpload(response.data.url);
    } catch (error) {
      console.error('Upload error:', error);
      alert('Upload failed');
    }
    setUploading(false);
  };

  if (compact) {
    return (
      <label className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded text-sm cursor-pointer inline-block">
        {uploading ? 'Uploading...' : 'Upload'}
        <input
          type="file"
          onChange={handleUpload}
          accept={accept}
          disabled={uploading}
          className="hidden"
        />
      </label>
    );
  }

  return (
    <div className="space-y-2">
      {label && <label className="block text-gray-300 text-sm">{label}</label>}
      {currentUrl && (
        <div className="mb-2">
          {accept?.includes('image') ? (
            <img
              src={currentUrl}
              alt="Preview"
              className="w-20 h-20 object-cover rounded"
            />
          ) : (
            <a
              href={currentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-amber-400 hover:underline text-sm"
            >
              View file
            </a>
          )}
        </div>
      )}
      <input
        type="file"
        onChange={handleUpload}
        accept={accept}
        disabled={uploading}
        className="w-full text-gray-300 text-sm file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:bg-gray-700 file:text-gray-300 hover:file:bg-gray-600"
      />
      {uploading && <p className="text-amber-400 text-sm">Uploading...</p>}
    </div>
  );
};

// ============== DASHBOARD TAB ==============
const DashboardTab = ({ getAuthHeaders }) => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get(`${API}/admin/dashboard/stats`, {
          headers: getAuthHeaders()
        });
        setStats(response.data);
      } catch (error) {
        console.error('Error fetching stats:', error);
      }
      setLoading(false);
    };
    fetchStats();
  }, [getAuthHeaders]);

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-white">Dashboard</h2>

      <div className="grid grid-cols-4 gap-6">
        <div className="bg-gray-800 rounded-lg p-6">
          <p className="text-gray-400 text-sm">Total Customers</p>
          <p className="text-3xl font-bold text-white">{stats?.total_customers || 0}</p>
        </div>
        <div className="bg-gray-800 rounded-lg p-6">
          <p className="text-gray-400 text-sm">Active Subscriptions</p>
          <p className="text-3xl font-bold text-green-400">{stats?.active_subscriptions || 0}</p>
        </div>
        <div className="bg-gray-800 rounded-lg p-6">
          <p className="text-gray-400 text-sm">Total Orders</p>
          <p className="text-3xl font-bold text-white">{stats?.total_orders || 0}</p>
        </div>
        <div className="bg-gray-800 rounded-lg p-6">
          <p className="text-gray-400 text-sm">Total Revenue</p>
          <p className="text-3xl font-bold text-amber-400">${stats?.total_revenue?.toFixed(2) || '0.00'}</p>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-6">
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Recent Orders</h3>
          {stats?.recent_orders?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_orders.map((order) => (
                <div key={order.id} className="flex justify-between items-center text-sm">
                  <span className="text-gray-300">{order.order_number}</span>
                  <span className="text-amber-400">${order.total?.toFixed(2)}</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">No orders yet</p>
          )}
        </div>

        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">Recent Customers</h3>
          {stats?.recent_customers?.length > 0 ? (
            <div className="space-y-3">
              {stats.recent_customers.map((customer) => (
                <div key={customer.id} className="flex justify-between items-center text-sm">
                  <span className="text-gray-300">{customer.email}</span>
                  <span className="text-gray-400">{customer.order_count || 0} orders</span>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-400 text-sm">No customers yet</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ============== HERO MEDIA TAB ==============
const HeroMediaTab = ({ getAuthHeaders }) => {
  const [settings, setSettings] = useState({
    enabled: false,
    media_type: 'slideshow',
    video_url: '',
    slideshow_images: [],
    slideshow_interval: 5000,
    transition_type: 'fade',
    show_frame: true,
    frame_style: 'antique_gold',
    text_overlay: {
      enabled: true,
      title: 'The Timeless Prophecy',
      subtitle: 'Letters from another world'
    },
    show_cta: false,
    cta_text: 'Begin the Correspondence',
    cta_link: '/membership'
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [message, setMessage] = useState(null);

  // Fetch current settings
  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const res = await axios.get(`${API}/admin/hero-media`, { headers: getAuthHeaders() });
        setSettings(res.data);
      } catch (error) {
        console.error('Error fetching hero media settings:', error);
      }
      setLoading(false);
    };
    fetchSettings();
  }, [getAuthHeaders]);

  // Save settings
  const handleSave = async () => {
    setSaving(true);
    setMessage(null);
    try {
      await axios.put(`${API}/admin/hero-media`, settings, { headers: getAuthHeaders() });
      setMessage({ type: 'success', text: 'Settings saved successfully!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    }
    setSaving(false);
  };

  // Handle image upload for slideshow
  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploadingImage(true);
    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/admin/media/upload`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
      });
      
      const newImage = {
        url: res.data.url,
        alt: file.name.replace(/\.[^/.]+$/, ''),
        id: Date.now().toString()
      };
      
      setSettings(prev => ({
        ...prev,
        slideshow_images: [...prev.slideshow_images, newImage]
      }));
      setMessage({ type: 'success', text: 'Image uploaded!' });
    } catch (error) {
      setMessage({ type: 'error', text: 'Failed to upload image' });
    }
    setUploadingImage(false);
  };

  // Remove image from slideshow
  const handleRemoveImage = (index) => {
    setSettings(prev => ({
      ...prev,
      slideshow_images: prev.slideshow_images.filter((_, i) => i !== index)
    }));
  };

  // Move image in slideshow order
  const handleMoveImage = (index, direction) => {
    const newImages = [...settings.slideshow_images];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newImages.length) return;
    [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
    setSettings(prev => ({ ...prev, slideshow_images: newImages }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Cinematic Hero Media</h2>
          <p className="text-gray-400 text-sm mt-1">Configure the video or slideshow for your homepage hero section</p>
        </div>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-amber-600 hover:bg-amber-500 disabled:bg-gray-600 text-white px-6 py-2 rounded-lg flex items-center gap-2"
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>

      {message && (
        <div className={`p-4 rounded-lg ${message.type === 'success' ? 'bg-green-900/50 text-green-400' : 'bg-red-900/50 text-red-400'}`}>
          {message.text}
        </div>
      )}

      {/* Enable/Disable Toggle */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-white font-semibold">Enable Cinematic Hero</h3>
            <p className="text-gray-400 text-sm">Show a video or slideshow in the hero section</p>
          </div>
          <button
            onClick={() => setSettings(prev => ({ ...prev, enabled: !prev.enabled }))}
            className={`relative w-14 h-7 rounded-full transition-colors ${settings.enabled ? 'bg-amber-600' : 'bg-gray-600'}`}
          >
            <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${settings.enabled ? 'translate-x-8' : 'translate-x-1'}`} />
          </button>
        </div>
      </div>

      {settings.enabled && (
        <>
          {/* Media Type Selection */}
          <div className="bg-gray-800 rounded-lg p-6">
            <h3 className="text-white font-semibold mb-4">Media Type</h3>
            <div className="grid grid-cols-2 gap-4">
              <button
                onClick={() => setSettings(prev => ({ ...prev, media_type: 'slideshow' }))}
                className={`p-4 rounded-lg border-2 transition-all ${
                  settings.media_type === 'slideshow' 
                    ? 'border-amber-500 bg-amber-500/10' 
                    : 'border-gray-600 hover:border-gray-500'
                }`}
              >
                <div className="text-3xl mb-2">🖼️</div>
                <div className="text-white font-medium">Image Slideshow</div>
                <p className="text-gray-400 text-sm mt-1">Multiple images with fade/pan transitions</p>
              </button>
              <button
                onClick={() => setSettings(prev => ({ ...prev, media_type: 'video' }))}
                className={`p-4 rounded-lg border-2 transition-all ${
                  settings.media_type === 'video' 
                    ? 'border-amber-500 bg-amber-500/10' 
                    : 'border-gray-600 hover:border-gray-500'
                }`}
              >
                <div className="text-3xl mb-2">🎬</div>
                <div className="text-white font-medium">Looping Video</div>
                <p className="text-gray-400 text-sm mt-1">Single video file, auto-plays muted</p>
              </button>
            </div>
          </div>

          {/* Video URL (if video type) */}
          {settings.media_type === 'video' && (
            <div className="bg-gray-800 rounded-lg p-6">
              <h3 className="text-white font-semibold mb-4">Video URL</h3>
              <input
                type="text"
                value={settings.video_url || ''}
                onChange={(e) => setSettings(prev => ({ ...prev, video_url: e.target.value }))}
                placeholder="https://example.com/your-video.mp4"
                className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white placeholder-gray-400"
              />
              <p className="text-gray-400 text-sm mt-2">
                Upload video to Media Library first, then paste the URL here. Recommended: MP4 format, under 10MB.
              </p>
            </div>
          )}

          {/* Slideshow Images (if slideshow type) */}
          {settings.media_type === 'slideshow' && (
            <div className="bg-gray-800 rounded-lg p-6">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-white font-semibold">Slideshow Images</h3>
                <label className="bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded-lg cursor-pointer flex items-center gap-2">
                  {uploadingImage ? 'Uploading...' : '+ Add Image'}
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                    disabled={uploadingImage}
                  />
                </label>
              </div>

              {settings.slideshow_images.length === 0 ? (
                <div className="border-2 border-dashed border-gray-600 rounded-lg p-8 text-center">
                  <div className="text-4xl mb-2">🖼️</div>
                  <p className="text-gray-400">No images yet. Add images to create your slideshow.</p>
                  <p className="text-gray-500 text-sm mt-1">Recommended: Vintage, moody images - letters, candlelight, wax seals</p>
                </div>
              ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {settings.slideshow_images.map((img, index) => (
                    <div key={img.id || index} className="relative group">
                      <img
                        src={img.url}
                        alt={img.alt}
                        className="w-full aspect-video object-cover rounded-lg"
                      />
                      <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg flex items-center justify-center gap-2">
                        {index > 0 && (
                          <button
                            onClick={() => handleMoveImage(index, 'up')}
                            className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded"
                          >
                            ←
                          </button>
                        )}
                        <button
                          onClick={() => handleRemoveImage(index)}
                          className="bg-red-600 hover:bg-red-500 text-white p-2 rounded"
                        >
                          ×
                        </button>
                        {index < settings.slideshow_images.length - 1 && (
                          <button
                            onClick={() => handleMoveImage(index, 'down')}
                            className="bg-gray-700 hover:bg-gray-600 text-white p-2 rounded"
                          >
                            →
                          </button>
                        )}
                      </div>
                      <input
                        type="text"
                        value={img.alt || ''}
                        onChange={(e) => {
                          const newImages = [...settings.slideshow_images];
                          newImages[index] = { ...newImages[index], alt: e.target.value };
                          setSettings(prev => ({ ...prev, slideshow_images: newImages }));
                        }}
                        placeholder="Alt text"
                        className="w-full mt-2 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                      />
                    </div>
                  ))}
                </div>
              )}

              {/* Slideshow Settings */}
              <div className="mt-6 pt-6 border-t border-gray-700 grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Slide Interval (seconds)</label>
                  <input
                    type="number"
                    min="2"
                    max="15"
                    value={settings.slideshow_interval / 1000}
                    onChange={(e) => setSettings(prev => ({ ...prev, slideshow_interval: parseInt(e.target.value) * 1000 }))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Transition Type</label>
                  <select
                    value={settings.transition_type}
                    onChange={(e) => setSettings(prev => ({ ...prev, transition_type: e.target.value }))}
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-2 text-white"
                  >
                    <option value="fade">Fade</option>
                    <option value="pan">Slow Pan & Zoom</option>
                  </select>
                </div>
              </div>
            </div>
          )}

          {/* Frame Style */}
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-white font-semibold">Decorative Frame</h3>
                <p className="text-gray-400 text-sm">Add a vintage-style frame around the media</p>
              </div>
              <button
                onClick={() => setSettings(prev => ({ ...prev, show_frame: !prev.show_frame }))}
                className={`relative w-14 h-7 rounded-full transition-colors ${settings.show_frame ? 'bg-amber-600' : 'bg-gray-600'}`}
              >
                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${settings.show_frame ? 'translate-x-8' : 'translate-x-1'}`} />
              </button>
            </div>

            {settings.show_frame && (
              <div className="grid grid-cols-3 gap-4 mt-4">
                {[
                  { id: 'antique_gold', label: 'Antique Gold', desc: 'Ornate golden frame' },
                  { id: 'dark_wood', label: 'Dark Wood', desc: 'Rich wooden frame' },
                  { id: 'simple', label: 'Simple', desc: 'Minimal gold border' }
                ].map(style => (
                  <button
                    key={style.id}
                    onClick={() => setSettings(prev => ({ ...prev, frame_style: style.id }))}
                    className={`p-4 rounded-lg border-2 transition-all text-left ${
                      settings.frame_style === style.id 
                        ? 'border-amber-500 bg-amber-500/10' 
                        : 'border-gray-600 hover:border-gray-500'
                    }`}
                  >
                    <div className="text-white font-medium">{style.label}</div>
                    <p className="text-gray-400 text-sm">{style.desc}</p>
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Text Overlay */}
          <div className="bg-gray-800 rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h3 className="text-white font-semibold">Text Overlay</h3>
                <p className="text-gray-400 text-sm">Show title and subtitle on the media</p>
              </div>
              <button
                onClick={() => setSettings(prev => ({ 
                  ...prev, 
                  text_overlay: { ...prev.text_overlay, enabled: !prev.text_overlay?.enabled }
                }))}
                className={`relative w-14 h-7 rounded-full transition-colors ${settings.text_overlay?.enabled ? 'bg-amber-600' : 'bg-gray-600'}`}
              >
                <div className={`absolute top-1 w-5 h-5 bg-white rounded-full transition-transform ${settings.text_overlay?.enabled ? 'translate-x-8' : 'translate-x-1'}`} />
              </button>
            </div>

            {settings.text_overlay?.enabled && (
              <div className="space-y-4 mt-4">
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Title</label>
                  <input
                    type="text"
                    value={settings.text_overlay?.title || ''}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      text_overlay: { ...prev.text_overlay, title: e.target.value }
                    }))}
                    placeholder="The Timeless Prophecy"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-300 text-sm mb-2">Subtitle</label>
                  <input
                    type="text"
                    value={settings.text_overlay?.subtitle || ''}
                    onChange={(e) => setSettings(prev => ({ 
                      ...prev, 
                      text_overlay: { ...prev.text_overlay, subtitle: e.target.value }
                    }))}
                    placeholder="Letters from another world"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white"
                  />
                </div>
              </div>
            )}
          </div>

          {/* CTA Button Settings */}
          <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-white font-semibold">Call-to-Action Button</h3>
              <button
                type="button"
                onClick={() => setSettings(prev => ({ ...prev, show_cta: !prev.show_cta }))}
                className={`relative w-12 h-6 rounded-full transition-colors ${settings.show_cta ? 'bg-gold-500' : 'bg-gray-600'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${settings.show_cta ? 'left-7' : 'left-1'}`} />
              </button>
            </div>
            {settings.show_cta && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Button Text</label>
                  <input
                    type="text"
                    value={settings.cta_text || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, cta_text: e.target.value }))}
                    placeholder="Begin the Correspondence"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Button Link</label>
                  <input
                    type="text"
                    value={settings.cta_link || ''}
                    onChange={(e) => setSettings(prev => ({ ...prev, cta_link: e.target.value }))}
                    placeholder="/membership"
                    className="w-full bg-gray-700 border border-gray-600 rounded-lg px-4 py-3 text-white"
                  />
                </div>
              </div>
            )}
          </div>

          {/* Instructions */}
          <div className="bg-gray-800/50 rounded-lg p-6 border border-gray-700">
            <h3 className="text-white font-semibold mb-3">📝 Usage Instructions</h3>
            <ol className="text-gray-400 text-sm space-y-2 list-decimal list-inside">
              <li>Go to <strong>Homepage Builder</strong> tab</li>
              <li>Add a new section with type <strong>"Cinematic Hero"</strong></li>
              <li>Position it where you want the media to appear</li>
              <li>The media settings here will be used for that section</li>
            </ol>
          </div>
        </>
      )}
    </div>
  );
};

// ============== HOMEPAGE BUILDER TAB ==============
const HomepageBuilderTab = ({ getAuthHeaders }) => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [formData, setFormData] = useState({
    section_type: 'hero',
    title: '',
    content: {},
    is_published: false
  });

  const sectionTypes = [
    { type: 'hero', label: 'Hero Section', description: 'Drag-and-drop layered hero with full creative control' },
    { type: 'cinematic_hero', label: 'Cinematic Hero', description: 'Video or slideshow media (configure in Hero Media tab)' },
    { type: 'prophecy', label: 'Prophecy/Quote', description: 'Featured quote or prophecy text' },
    { type: 'coming_soon', label: 'Coming Soon', description: 'Teaser for upcoming content' },
    { type: 'cta', label: 'Call to Action', description: 'CTA section with button' },
    { type: 'atmosphere', label: 'Image/Atmosphere', description: 'Full-width cinematic image for immersion' },
    { type: 'featured_cards', label: 'Featured Cards', description: 'Grid of cards with image, title, text, and link' },
    { type: 'image_gallery', label: 'Image Gallery', description: 'Swipeable image carousel with captions' },
    { type: 'slideshow', label: 'Slideshow', description: 'Elegant auto-playing image slideshow with fade transitions' },
    { type: 'expandable_narrative', label: 'Expandable Narrative', description: 'Full-length description with expand/collapse toggle' },
    { type: 'how_it_works', label: 'How It Works', description: 'Explains the correspondence experience with optional steps' },
    { type: 'reader_impressions', label: 'Reader Impressions', description: 'Testimonials and reviews from readers' },
    { type: 'first_letter_preview', label: 'First Letter Preview', description: 'Teaser excerpt from a character letter' },
    { type: 'scrolling_marquee', label: 'Scrolling Marquee Banner', description: 'Infinite scrolling text banner — visual separator between sections' },
    { type: 'editorial_story', label: 'Editorial Story Block', description: 'Two-column image + narrative block — stackable, alternating layouts' },
    { type: 'feature_spotlight', label: 'Feature Spotlight', description: 'Centered showcase — large image, narrative, and CTA for any product, character, or experience' }
  ];

  const getDefaultContent = (type) => {
    switch (type) {
      case 'hero':
        return { 
          settings: {
            height_desktop: '100vh',
            height_tablet: '80vh',
            height_mobile: '70vh',
            background_image: '',
            background_video: '',
            background_parallax: false,
            background_overlay_color: '#000000',
            background_overlay_opacity: 30,
            background_blur: 0,
            background_position: 'center center',
          },
          layers: []
        };
      case 'cinematic_hero':
        return {
          note: 'Configure video/slideshow settings in Hero Media tab'
        };
      case 'prophecy':
        return { quote: '', attribution: '' };
      case 'coming_soon':
        return { section_label: 'COMING SOON', heading: 'More Voices Await', description: '' };
      case 'cta':
        return { heading: '', description: '', button_text: 'Learn More', button_link: '/membership', background_image: '' };
      case 'atmosphere':
        return { image_url: '', overlay_text: '', caption: '', height: 'medium' };
      case 'featured_cards':
        return { 
          section_label: '', 
          heading: '', 
          description: '', 
          cards: [
            { image_url: '', title: '', text: '', link_url: '', link_text: 'Learn More' }
          ]
        };
      case 'image_gallery':
        return {
          heading: '',
          images: []
        };
      case 'slideshow':
        return {
          title: '',
          description: '',
          images: [],
          autoplay: true,
          autoplay_interval: 4, // seconds
          show_arrows: true,
          show_dots: true
        };
      case 'expandable_narrative':
        return {
          heading: '',
          body: '',
          preview_lines: 4,
          enable_expand: true
        };
      case 'how_it_works':
        return {
          heading: 'How The Correspondence Works',
          subtitle: 'YOUR JOURNEY',
          body: '<p>When you subscribe, you become a correspondent in an unfolding mystery.</p>',
          bg_color: '#FAF7F2',
          bg_image_url: '',
          bg_image_opacity: 15,
          bg_image_enabled: false,
          bg_overlay_color: '',
          timeline_color: '#C7A15A',
          marker_style: 'seal',
          marker_size: 64,
          steps: [
            { title: 'Choose Your Correspondent', subtitle: 'STEP ONE', description: 'Select from our cast of mysterious characters.', image_url: '', image_alt: '', button_text: '', button_link: '', layout: 'text_left' },
            { title: 'Receive Your First Letter', subtitle: 'STEP TWO', description: 'Within moments, your first letter arrives — sealed and waiting.', image_url: '', image_alt: '', button_text: '', button_link: '', layout: 'text_right' },
            { title: 'Continue The Correspondence', subtitle: 'STEP THREE', description: 'Every 30 days, a new letter deepens the mystery.', image_url: '', image_alt: '', button_text: '', button_link: '', layout: 'text_left' },
            { title: 'Become Part of The Story', subtitle: 'STEP FOUR', description: 'The characters remember. The story evolves.', image_url: '', image_alt: '', button_text: 'Begin Your Journey', button_link: '/membership', layout: 'text_right' }
          ]
        };
      case 'reader_impressions':
        return {
          heading: 'Reader Impressions',
          testimonials: [
            { title: 'A truly immersive experience', body: 'I felt like I was receiving letters from an old friend who happened to live in another century. The anticipation of each new correspondence became the highlight of my month.', reader_name: 'E.M.', reader_location: 'Boston' },
            { title: 'Unlike anything else', body: 'The Timeless Post has rekindled my love for storytelling. Each letter feels so personal, so carefully crafted. It\'s like being let in on a secret.', reader_name: 'R.C.', reader_location: 'London' },
            { title: 'Beautiful and mysterious', body: 'From the moment I opened my first letter, I was hooked. The writing is evocative, the mystery is compelling, and the whole experience feels wonderfully anachronistic.', reader_name: 'S.K.', reader_location: 'Melbourne' }
          ],
          footer_text: 'Early readers who experienced the first correspondences.',
          columns: 3,
          show_dividers: true
        };
      case 'first_letter_preview':
        return {
          heading: 'A Glimpse Into the Correspondence',
          intro_text: 'Before you commit to this journey, perhaps a taste of what awaits...',
          letter_excerpt: '<p>My dearest friend,</p><p>I write to you now from the quiet hours before dawn, when the house sleeps and only the scratch of my pen keeps me company. There are things I must tell you—things I dare not speak aloud, lest the walls themselves carry my secrets to unwelcome ears.</p><p>It began three weeks past, on the night of the autumn moon. I saw something in the garden that I cannot explain, something that has haunted my every waking moment since...</p>',
          character_name: 'Sienna Mae',
          continuation_note: 'Subscribe to receive the full letter and continue the correspondence.',
          show_cta: true,
          cta_text: 'Begin the Correspondence',
          cta_link: '/membership',
          show_dividers: true
        };
      case 'scrolling_marquee':
        return {
          text: 'Unplug • Slow Down • Open An Envelope • Discover A New World • Read By Lamplight • Collect Stories • Escape The Scroll •',
          bg_color: '#17352D',
          text_color: '#C7A15A',
          speed: 30,
          font_size: 18,
          glow: true,
          spacing_top: 0,
          spacing_bottom: 0
        };
      case 'editorial_story':
        return {
          layout: 'alternate',
          bg_color: 'transparent',
          vertical_align: 'center',
          show_frame: false,
          spacing_top: 40,
          spacing_bottom: 40,
          blocks: [
            {
              image_url: '',
              image_caption: '',
              image_position: 'center',
              small_heading: 'THE STORY',
              main_heading: 'Letters From Another World',
              narrative: '<p>Each month, a letter arrives in your inbox — or at your door. It is handwritten by a character whose world is not quite your own, yet feels strangely familiar.</p>',
              button_text: 'Begin Your Journey',
              button_link: '/membership',
              layout: 'image_left'
            }
          ]
        };
      case 'feature_spotlight':
        return {
          title: 'Featured Experience',
          subtitle: 'SPOTLIGHT',
          image_url: '',
          image_alt: '',
          image_caption: '',
          image_corner: 'square',
          show_frame: false,
          show_divider: true,
          description: '<p>Discover something extraordinary within The Timeless Post.</p>',
          button_text: 'Explore Now',
          button_link: '/membership',
          button_style: 'outline',
          bg_color: '#FAF7F2',
          bg_image_url: '',
          bg_image_enabled: false,
          bg_image_opacity: 10,
          section_width: 'default',
          spacing_top: 48,
          spacing_bottom: 48
        };
      default:
        return {};
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/homepage-sections`, { headers: getAuthHeaders() });
      setSections(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/homepage-sections/${editingId}`, formData, { headers: getAuthHeaders() });
      } else {
        const newSection = {
          ...formData,
          display_order: sections.length
        };
        await axios.post(`${API}/admin/homepage-sections`, newSection, { headers: getAuthHeaders() });
      }
      fetchData();
      resetForm();
      setShowAddModal(false);
    } catch (error) {
      alert('Error saving section');
    }
  };

  const handleEdit = (section) => {
    setEditingId(section.id);
    setFormData({
      section_type: section.section_type,
      title: section.title,
      content: section.content || {},
      is_published: section.is_published
    });
    setShowAddModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this section?')) return;
    await axios.delete(`${API}/admin/homepage-sections/${id}`, { headers: getAuthHeaders() });
    fetchData();
  };

  const handleTogglePublish = async (section) => {
    await axios.put(`${API}/admin/homepage-sections/${section.id}`, 
      { is_published: !section.is_published }, 
      { headers: getAuthHeaders() }
    );
    fetchData();
  };

  const handleMoveUp = async (index) => {
    if (index === 0) return;
    const newSections = [...sections];
    [newSections[index - 1], newSections[index]] = [newSections[index], newSections[index - 1]];
    const sectionIds = newSections.map(s => s.id);
    await axios.post(`${API}/admin/homepage-sections/reorder`, sectionIds, { headers: getAuthHeaders() });
    fetchData();
  };

  const handleMoveDown = async (index) => {
    if (index === sections.length - 1) return;
    const newSections = [...sections];
    [newSections[index], newSections[index + 1]] = [newSections[index + 1], newSections[index]];
    const sectionIds = newSections.map(s => s.id);
    await axios.post(`${API}/admin/homepage-sections/reorder`, sectionIds, { headers: getAuthHeaders() });
    fetchData();
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({ section_type: 'hero', title: '', content: {}, is_published: false });
  };

  const updateContent = (key, value) => {
    setFormData({ ...formData, content: { ...formData.content, [key]: value } });
  };

  const renderContentFields = () => {
    const type = formData.section_type;
    const content = formData.content;

    switch (type) {
      case 'hero':
        return (
          <HeroBuilder
            content={formData.content}
            onChange={(newContent) => setFormData(prev => ({ ...prev, content: newContent }))}
            getAuthHeaders={getAuthHeaders}
          />
        );
      case 'prophecy':
        return (
          <>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Quote</label>
              <textarea value={content.quote || ''} onChange={(e) => updateContent('quote', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm h-32" />
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Attribution</label>
              <input type="text" value={content.attribution || ''} onChange={(e) => updateContent('attribution', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" placeholder="— Author Name" />
            </div>
          </>
        );
      case 'featured_characters':
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-1">Section Label</label>
                <input type="text" value={content.section_label || ''} onChange={(e) => updateContent('section_label', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1">Show Count</label>
                <input type="number" value={content.show_count || 2} onChange={(e) => updateContent('show_count', parseInt(e.target.value))} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Heading</label>
              <input type="text" value={content.heading || ''} onChange={(e) => updateContent('heading', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Description</label>
              <textarea value={content.description || ''} onChange={(e) => updateContent('description', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm h-20" />
            </div>
          </>
        );
      case 'coming_soon':
        return (
          <>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Section Label</label>
              <input type="text" value={content.section_label || ''} onChange={(e) => updateContent('section_label', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Heading</label>
              <input type="text" value={content.heading || ''} onChange={(e) => updateContent('heading', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Description</label>
              <textarea value={content.description || ''} onChange={(e) => updateContent('description', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm h-20" />
            </div>
          </>
        );
      case 'cta':
        return (
          <>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Heading</label>
              <input type="text" value={content.heading || ''} onChange={(e) => updateContent('heading', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Description</label>
              <textarea value={content.description || ''} onChange={(e) => updateContent('description', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm h-20" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-1">Button Text</label>
                <input type="text" value={content.button_text || ''} onChange={(e) => updateContent('button_text', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1">Button Link</label>
                <input type="text" value={content.button_link || ''} onChange={(e) => updateContent('button_link', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Background Image URL</label>
              <input type="text" value={content.background_image || ''} onChange={(e) => updateContent('background_image', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
            </div>
          </>
        );
      case 'atmosphere':
        return (
          <>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Image URL</label>
              <input type="text" value={content.image_url || ''} onChange={(e) => updateContent('image_url', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" placeholder="Full-width cinematic image" />
              {content.image_url && <img src={content.image_url} alt="Preview" className="mt-2 max-h-32 rounded" />}
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Overlay Text (optional)</label>
              <input type="text" value={content.overlay_text || ''} onChange={(e) => updateContent('overlay_text', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" placeholder="Text displayed over the image" />
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Caption (optional)</label>
              <input type="text" value={content.caption || ''} onChange={(e) => updateContent('caption', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Height</label>
              <select value={content.height || 'medium'} onChange={(e) => updateContent('height', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm">
                <option value="small">Small (300px)</option>
                <option value="medium">Medium (500px)</option>
                <option value="large">Large (700px)</option>
                <option value="full">Full Screen</option>
              </select>
            </div>
          </>
        );
      case 'featured_cards':
        const cards = content.cards || [{ image_url: '', title: '', text: '', link_url: '', link_text: 'Learn More' }];
        const updateCard = (index, field, value) => {
          const newCards = [...cards];
          newCards[index] = { ...newCards[index], [field]: value };
          updateContent('cards', newCards);
        };
        const addCard = () => {
          updateContent('cards', [...cards, { image_url: '', title: '', text: '', link_url: '', link_text: 'Learn More' }]);
        };
        const removeCard = (index) => {
          updateContent('cards', cards.filter((_, i) => i !== index));
        };
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-1">Section Label</label>
                <input type="text" value={content.section_label || ''} onChange={(e) => updateContent('section_label', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" placeholder="e.g. THE CHARACTERS" />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1">Heading</label>
                <input type="text" value={content.heading || ''} onChange={(e) => updateContent('heading', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Description</label>
              <textarea value={content.description || ''} onChange={(e) => updateContent('description', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm h-16" />
            </div>
            <div className="border-t border-gray-600 pt-4 mt-4">
              <div className="flex justify-between items-center mb-3">
                <label className="block text-gray-300 text-sm font-medium">Cards ({cards.length})</label>
                <button type="button" onClick={addCard} className="text-xs bg-gold-500 text-charcoal-900 px-3 py-1 rounded hover:bg-gold-400">+ Add Card</button>
              </div>
              {cards.map((card, index) => (
                <div key={index} className="bg-gray-700/50 rounded p-3 mb-3">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-gray-400 text-xs">Card {index + 1}</span>
                    {cards.length > 1 && (
                      <button type="button" onClick={() => removeCard(index)} className="text-red-400 text-xs hover:text-red-300">Remove</button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 gap-2 mb-2">
                    <input type="text" value={card.image_url || ''} onChange={(e) => updateCard(index, 'image_url', e.target.value)} className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-xs" placeholder="Image URL" />
                    <input type="text" value={card.title || ''} onChange={(e) => updateCard(index, 'title', e.target.value)} className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-xs" placeholder="Title" />
                  </div>
                  <textarea value={card.text || ''} onChange={(e) => updateCard(index, 'text', e.target.value)} className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-xs mb-2 h-12" placeholder="Short description" />
                  <div className="grid grid-cols-2 gap-2">
                    <input type="text" value={card.link_url || ''} onChange={(e) => updateCard(index, 'link_url', e.target.value)} className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-xs" placeholder="Link URL (optional)" />
                    <input type="text" value={card.link_text || ''} onChange={(e) => updateCard(index, 'link_text', e.target.value)} className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-xs" placeholder="Link Text" />
                  </div>
                </div>
              ))}
            </div>
          </>
        );
      case 'image_gallery':
        const galleryImages = content.images || [];
        const addGalleryImage = () => {
          updateContent('images', [...galleryImages, { url: '', caption: '' }]);
        };
        const updateGalleryImage = (index, field, value) => {
          const newImages = [...galleryImages];
          newImages[index] = { ...newImages[index], [field]: value };
          updateContent('images', newImages);
        };
        const removeGalleryImage = (index) => {
          updateContent('images', galleryImages.filter((_, i) => i !== index));
        };
        const moveGalleryImage = (index, direction) => {
          const newImages = [...galleryImages];
          const newIndex = direction === 'up' ? index - 1 : index + 1;
          if (newIndex < 0 || newIndex >= newImages.length) return;
          [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
          updateContent('images', newImages);
        };
        return (
          <>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Section Heading (Optional)</label>
              <input type="text" value={content.heading || ''} onChange={(e) => updateContent('heading', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" placeholder="e.g. Gallery" />
            </div>
            <div className="border-t border-gray-600 pt-4 mt-4">
              <div className="flex justify-between items-center mb-3">
                <label className="block text-gray-300 text-sm font-medium">Images ({galleryImages.length})</label>
                <button type="button" onClick={addGalleryImage} className="text-xs bg-gold-500 text-charcoal-900 px-3 py-1 rounded hover:bg-gold-400">+ Add Image</button>
              </div>
              {galleryImages.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4">No images added. Click "Add Image" to start.</p>
              )}
              {galleryImages.map((img, index) => (
                <div key={index} className="bg-gray-700/50 rounded p-3 mb-3">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs">Image {index + 1}</span>
                      <div className="flex gap-1">
                        <button type="button" onClick={() => moveGalleryImage(index, 'up')} disabled={index === 0} className="text-gray-400 hover:text-white disabled:opacity-30 text-xs px-1">↑</button>
                        <button type="button" onClick={() => moveGalleryImage(index, 'down')} disabled={index === galleryImages.length - 1} className="text-gray-400 hover:text-white disabled:opacity-30 text-xs px-1">↓</button>
                      </div>
                    </div>
                    <button type="button" onClick={() => removeGalleryImage(index)} className="text-red-400 text-xs hover:text-red-300">Remove</button>
                  </div>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        value={img.url || ''} 
                        onChange={(e) => updateGalleryImage(index, 'url', e.target.value)} 
                        className="flex-1 bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-xs" 
                        placeholder="Image URL (paste or use Media Library)" 
                      />
                      <FileUpload 
                        uploadType="gallery" 
                        onUpload={(url) => updateGalleryImage(index, 'url', url)}
                        accept="image/*"
                        compact={true}
                      />
                    </div>
                    <input 
                      type="text" 
                      value={img.caption || ''} 
                      onChange={(e) => updateGalleryImage(index, 'caption', e.target.value)} 
                      className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-xs" 
                      placeholder="Caption (optional)" 
                    />
                    {img.url && (
                      <img src={img.url} alt={img.caption || 'Preview'} className="w-full h-24 object-cover rounded mt-1" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        );
      case 'slideshow':
        const slideshowImages = content.images || [];
        const addSlideshowImage = () => {
          updateContent('images', [...slideshowImages, { url: '', alt: '' }]);
        };
        const updateSlideshowImage = (index, field, value) => {
          const newImages = [...slideshowImages];
          newImages[index] = { ...newImages[index], [field]: value };
          updateContent('images', newImages);
        };
        const removeSlideshowImage = (index) => {
          updateContent('images', slideshowImages.filter((_, i) => i !== index));
        };
        const moveSlideshowImage = (index, direction) => {
          const newImages = [...slideshowImages];
          const newIndex = direction === 'up' ? index - 1 : index + 1;
          if (newIndex < 0 || newIndex >= newImages.length) return;
          [newImages[index], newImages[newIndex]] = [newImages[newIndex], newImages[index]];
          updateContent('images', newImages);
        };
        return (
          <>
            {/* Title and Description */}
            <div className="grid grid-cols-1 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-1">Section Title (Optional)</label>
                <input 
                  type="text" 
                  value={content.title || ''} 
                  onChange={(e) => updateContent('title', e.target.value)} 
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" 
                  placeholder="e.g. A Journey Through Time" 
                />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1">Description (Optional)</label>
                <textarea 
                  value={content.description || ''} 
                  onChange={(e) => updateContent('description', e.target.value)} 
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" 
                  placeholder="A brief description to appear below the title..."
                  rows={2}
                />
              </div>
            </div>

            {/* Slideshow Settings */}
            <div className="border-t border-gray-600 pt-4 mt-4">
              <h4 className="text-white font-medium text-sm mb-3">Slideshow Settings</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between bg-gray-700/50 rounded px-3 py-2">
                  <span className="text-gray-300 text-sm">Autoplay</span>
                  <button
                    type="button"
                    onClick={() => updateContent('autoplay', !content.autoplay)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${content.autoplay !== false ? 'bg-gold-500' : 'bg-gray-600'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${content.autoplay !== false ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>
                <div className="flex items-center gap-2 bg-gray-700/50 rounded px-3 py-2">
                  <span className="text-gray-300 text-sm">Interval</span>
                  <input
                    type="number"
                    min="1"
                    max="30"
                    value={content.autoplay_interval || 4}
                    onChange={(e) => updateContent('autoplay_interval', parseInt(e.target.value) || 4)}
                    className="w-16 bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm text-center"
                  />
                  <span className="text-gray-400 text-xs">sec</span>
                </div>
                <div className="flex items-center justify-between bg-gray-700/50 rounded px-3 py-2">
                  <span className="text-gray-300 text-sm">Show Arrows</span>
                  <button
                    type="button"
                    onClick={() => updateContent('show_arrows', !content.show_arrows)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${content.show_arrows !== false ? 'bg-gold-500' : 'bg-gray-600'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${content.show_arrows !== false ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>
                <div className="flex items-center justify-between bg-gray-700/50 rounded px-3 py-2">
                  <span className="text-gray-300 text-sm">Show Dots</span>
                  <button
                    type="button"
                    onClick={() => updateContent('show_dots', !content.show_dots)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${content.show_dots !== false ? 'bg-gold-500' : 'bg-gray-600'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${content.show_dots !== false ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>
              </div>
            </div>

            {/* Images */}
            <div className="border-t border-gray-600 pt-4 mt-4">
              <div className="flex justify-between items-center mb-3">
                <label className="block text-gray-300 text-sm font-medium">Images ({slideshowImages.length})</label>
                <button type="button" onClick={addSlideshowImage} className="text-xs bg-gold-500 text-charcoal-900 px-3 py-1 rounded hover:bg-gold-400">+ Add Image</button>
              </div>
              <p className="text-gray-500 text-xs mb-3">Recommended: 16:9 aspect ratio images for best results. Use arrows to reorder.</p>
              {slideshowImages.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-4 bg-gray-700/30 rounded">No images added. Click "Add Image" to start building your slideshow.</p>
              )}
              {slideshowImages.map((img, index) => (
                <div key={index} className="bg-gray-700/50 rounded p-3 mb-3">
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-gray-400 text-xs font-medium">Slide {index + 1}</span>
                      <div className="flex gap-1">
                        <button 
                          type="button" 
                          onClick={() => moveSlideshowImage(index, 'up')} 
                          disabled={index === 0} 
                          className="text-gray-400 hover:text-white disabled:opacity-30 text-xs px-2 py-1 bg-gray-600 rounded"
                          title="Move up"
                        >↑</button>
                        <button 
                          type="button" 
                          onClick={() => moveSlideshowImage(index, 'down')} 
                          disabled={index === slideshowImages.length - 1} 
                          className="text-gray-400 hover:text-white disabled:opacity-30 text-xs px-2 py-1 bg-gray-600 rounded"
                          title="Move down"
                        >↓</button>
                      </div>
                    </div>
                    <button type="button" onClick={() => removeSlideshowImage(index)} className="text-red-400 text-xs hover:text-red-300">Remove</button>
                  </div>
                  <div className="space-y-2">
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        value={img.url || ''} 
                        onChange={(e) => updateSlideshowImage(index, 'url', e.target.value)} 
                        className="flex-1 bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-xs" 
                        placeholder="Image URL (paste or use Media Library)" 
                      />
                      <FileUpload 
                        uploadType="slideshow" 
                        onUpload={(url) => updateSlideshowImage(index, 'url', url)}
                        accept="image/*"
                        compact={true}
                      />
                    </div>
                    <input 
                      type="text" 
                      value={img.alt || ''} 
                      onChange={(e) => updateSlideshowImage(index, 'alt', e.target.value)} 
                      className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-xs" 
                      placeholder="Alt text / description (for accessibility)" 
                    />
                    {img.url && (
                      <div className="relative">
                        <img src={img.url} alt={img.alt || 'Preview'} className="w-full h-32 object-cover rounded mt-1" />
                        <span className="absolute bottom-2 right-2 bg-black/60 text-white text-xs px-2 py-1 rounded">16:9 preview</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </>
        );
      case 'expandable_narrative':
        return (
          <>
            {/* Heading */}
            <div>
              <label className="block text-gray-300 text-sm mb-1">Heading (Optional)</label>
              <input 
                type="text" 
                value={content.heading || ''} 
                onChange={(e) => updateContent('heading', e.target.value)} 
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" 
                placeholder="e.g. The Story Behind The Timeless Prophecy" 
              />
            </div>

            {/* Background Image */}
            <div className="border border-gold-500/30 rounded-lg p-4 bg-gray-800/50">
              <label className="block text-gold-400 text-sm mb-2 font-medium">Background Image (Optional)</label>
              <p className="text-gray-500 text-xs mb-3">
                Add an atmospheric background image for an immersive effect. A dark overlay will be applied automatically for readability.
              </p>
              <input 
                type="text" 
                value={content.background_image_url || ''} 
                onChange={(e) => updateContent('background_image_url', e.target.value)} 
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" 
                placeholder="https://example.com/atmospheric-image.jpg" 
              />
              {content.background_image_url && (
                <div className="mt-3 relative rounded overflow-hidden" style={{ height: '120px' }}>
                  <img 
                    src={content.background_image_url} 
                    alt="Background preview" 
                    className="w-full h-full object-cover"
                    onError={(e) => e.target.style.display = 'none'}
                  />
                  <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/80 to-black/90 flex items-center justify-center">
                    <span className="text-parchment-100 text-sm font-serif">Preview with overlay</span>
                  </div>
                </div>
              )}
            </div>

            {/* Rich Text Body */}
            <div>
              <label className="block text-gray-300 text-sm mb-1">Narrative Body</label>
              <p className="text-gray-500 text-xs mb-2">
                Supports HTML formatting: &lt;b&gt;bold&lt;/b&gt;, &lt;i&gt;italic&lt;/i&gt;, &lt;br&gt; for line breaks, &lt;p&gt; for paragraphs
              </p>
              <textarea 
                value={content.body || ''} 
                onChange={(e) => updateContent('body', e.target.value)} 
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm font-mono" 
                placeholder="Enter your narrative content here. You can use HTML tags for formatting..."
                rows={12}
              />
              {/* Preview */}
              {content.body && (
                <div className="mt-3 p-4 bg-gray-800 rounded border border-gray-700">
                  <p className="text-gray-400 text-xs mb-2">Preview:</p>
                  <div 
                    className="text-gray-200 text-sm prose prose-invert max-w-none"
                    dangerouslySetInnerHTML={{ __html: content.body }}
                  />
                </div>
              )}
            </div>

            {/* Settings */}
            <div className="border-t border-gray-600 pt-4 mt-4">
              <h4 className="text-white font-medium text-sm mb-3">Display Settings</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center justify-between bg-gray-700/50 rounded px-3 py-2">
                  <span className="text-gray-300 text-sm">Enable Expand/Collapse</span>
                  <button
                    type="button"
                    onClick={() => updateContent('enable_expand', !content.enable_expand)}
                    className={`relative w-12 h-6 rounded-full transition-colors ${content.enable_expand !== false ? 'bg-gold-500' : 'bg-gray-600'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${content.enable_expand !== false ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>
                <div className="flex items-center gap-2 bg-gray-700/50 rounded px-3 py-2">
                  <span className="text-gray-300 text-sm">Preview Lines</span>
                  <input
                    type="number"
                    min="2"
                    max="10"
                    value={content.preview_lines || 4}
                    onChange={(e) => updateContent('preview_lines', parseInt(e.target.value) || 4)}
                    className="w-16 bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm text-center"
                  />
                </div>
              </div>
              <p className="text-gray-500 text-xs mt-2">
                When expand is enabled, only the first few lines will be shown initially with a "Read More" toggle.
              </p>
            </div>
          </>
        );
      case 'how_it_works':
        return (
          <>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-1">Section Subtitle</label>
                <input type="text" value={content.subtitle || ''} onChange={(e) => updateContent('subtitle', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" placeholder="YOUR JOURNEY" />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1">Section Heading</label>
                <input type="text" value={content.heading || ''} onChange={(e) => updateContent('heading', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
              </div>
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Introduction (HTML)</label>
              <textarea value={content.body || ''} onChange={(e) => updateContent('body', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" rows={2} />
            </div>

            {/* Background Controls */}
            <div className="border border-gray-600 rounded-lg p-3 space-y-3">
              <h4 className="text-white font-medium text-sm">Background</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Background Colour</label>
                  <div className="flex gap-2">
                    <input type="color" value={content.bg_color || '#FAF7F2'} onChange={(e) => updateContent('bg_color', e.target.value)} className="w-10 h-9 rounded border border-gray-600 cursor-pointer" />
                    <input type="text" value={content.bg_color || '#FAF7F2'} onChange={(e) => updateContent('bg_color', e.target.value)} className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs" />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Overlay Colour</label>
                  <div className="flex gap-2">
                    <input type="color" value={content.bg_overlay_color || '#000000'} onChange={(e) => updateContent('bg_overlay_color', e.target.value)} className="w-10 h-9 rounded border border-gray-600 cursor-pointer" />
                    <button onClick={() => updateContent('bg_overlay_color', '')} className={`text-xs px-2 rounded ${!content.bg_overlay_color ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}>None</button>
                  </div>
                </div>
              </div>
              <label className="flex items-center gap-2 text-gray-300 text-sm cursor-pointer">
                <input type="checkbox" checked={content.bg_image_enabled !== false && !!content.bg_image_url} onChange={(e) => updateContent('bg_image_enabled', e.target.checked)} className="rounded bg-gray-700" />
                Enable Background Image
              </label>
              {content.bg_image_enabled !== false && (
                <>
                  <FileUpload uploadType="media" onUpload={(url) => { updateContent('bg_image_url', url); updateContent('bg_image_enabled', true); }} currentUrl={content.bg_image_url} label="Background Image" accept="image/*" />
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Image Opacity ({content.bg_image_opacity ?? 15}%)</label>
                    <input type="range" min="5" max="100" value={content.bg_image_opacity ?? 15} onChange={(e) => updateContent('bg_image_opacity', parseInt(e.target.value))} className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer" />
                  </div>
                </>
              )}
            </div>

            {/* Timeline Controls */}
            <div className="border border-gray-600 rounded-lg p-3 space-y-3">
              <h4 className="text-white font-medium text-sm">Timeline & Markers</h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Timeline Colour</label>
                  <div className="flex gap-1">
                    <input type="color" value={content.timeline_color || '#C7A15A'} onChange={(e) => updateContent('timeline_color', e.target.value)} className="w-9 h-8 rounded border border-gray-600 cursor-pointer" />
                    <input type="text" value={content.timeline_color || '#C7A15A'} onChange={(e) => updateContent('timeline_color', e.target.value)} className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs" />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Marker Style</label>
                  <select value={content.marker_style || 'seal'} onChange={(e) => updateContent('marker_style', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs">
                    <option value="seal">Wax Seal</option>
                    <option value="compass">Compass</option>
                    <option value="stamp">Postage Stamp</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Marker Size ({content.marker_size || 64}px)</label>
                  <input type="range" min="40" max="96" value={content.marker_size || 64} onChange={(e) => updateContent('marker_size', parseInt(e.target.value))} className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer" />
                </div>
              </div>
            </div>

            {/* Steps */}
            <div className="border-t border-gray-700 pt-4">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-white font-medium text-sm">Journey Steps ({(content.steps || []).length})</h4>
                <button type="button" onClick={() => { const s = [...(content.steps || [])]; s.push({ title: '', subtitle: '', description: '', image_url: '', image_alt: '', button_text: '', button_link: '', layout: 'text_left' }); updateContent('steps', s); }} className="text-xs bg-green-700 hover:bg-green-600 text-white px-3 py-1.5 rounded">+ Add Step</button>
              </div>
              {(content.steps || []).map((step, idx) => (
                <div key={idx} className="bg-gray-750 border border-gray-600 rounded-lg p-4 mb-3 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-sm font-medium">Step {idx + 1}</span>
                    <div className="flex gap-2">
                      {idx > 0 && <button type="button" onClick={() => { const s = [...content.steps]; [s[idx-1], s[idx]] = [s[idx], s[idx-1]]; updateContent('steps', s); }} className="text-xs text-gray-400 hover:text-white">Up</button>}
                      {idx < (content.steps || []).length - 1 && <button type="button" onClick={() => { const s = [...content.steps]; [s[idx], s[idx+1]] = [s[idx+1], s[idx]]; updateContent('steps', s); }} className="text-xs text-gray-400 hover:text-white">Down</button>}
                      <button type="button" onClick={() => { const s = [...content.steps]; s.push({...step}); updateContent('steps', s); }} className="text-xs text-blue-400 hover:text-blue-300">Duplicate</button>
                      <button type="button" onClick={() => { if (window.confirm('Remove?')) { const s = [...content.steps]; s.splice(idx, 1); updateContent('steps', s); }}} className="text-xs text-red-400 hover:text-red-300">Remove</button>
                    </div>
                  </div>
                  <FileUpload uploadType="media" onUpload={(url) => { const s = [...content.steps]; s[idx] = {...s[idx], image_url: url}; updateContent('steps', s); }} currentUrl={step.image_url} label="Step Image" accept="image/*" />
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Image Alt Text</label>
                    <input type="text" value={step.image_alt || ''} onChange={(e) => { const s = [...content.steps]; s[idx] = {...s[idx], image_alt: e.target.value}; updateContent('steps', s); }} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs" placeholder="Describe the image" />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Small Heading</label>
                      <input type="text" value={step.subtitle || ''} onChange={(e) => { const s = [...content.steps]; s[idx] = {...s[idx], subtitle: e.target.value}; updateContent('steps', s); }} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs" placeholder="STEP ONE" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Main Heading</label>
                      <input type="text" value={step.title || ''} onChange={(e) => { const s = [...content.steps]; s[idx] = {...s[idx], title: e.target.value}; updateContent('steps', s); }} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Description</label>
                    <textarea value={step.description || ''} onChange={(e) => { const s = [...content.steps]; s[idx] = {...s[idx], description: e.target.value}; updateContent('steps', s); }} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm" rows={2} />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Layout</label>
                      <select value={step.layout || 'text_left'} onChange={(e) => { const s = [...content.steps]; s[idx] = {...s[idx], layout: e.target.value}; updateContent('steps', s); }} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs">
                        <option value="text_left">Text Left / Image Right</option>
                        <option value="text_right">Image Left / Text Right</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Button Text</label>
                      <input type="text" value={step.button_text || ''} onChange={(e) => { const s = [...content.steps]; s[idx] = {...s[idx], button_text: e.target.value}; updateContent('steps', s); }} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Button Link</label>
                      <input type="text" value={step.button_link || ''} onChange={(e) => { const s = [...content.steps]; s[idx] = {...s[idx], button_link: e.target.value}; updateContent('steps', s); }} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs" placeholder="/membership" />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        );
      case 'reader_impressions':
        return (
          <>
            {/* Section Heading */}
            <div>
              <label className="block text-gray-300 text-sm mb-1">Section Heading</label>
              <input 
                type="text" 
                value={content.heading || ''} 
                onChange={(e) => updateContent('heading', e.target.value)} 
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" 
                placeholder="Reader Impressions" 
              />
            </div>

            {/* Columns Selection */}
            <div className="flex items-center gap-4 bg-gray-700/50 rounded px-3 py-2">
              <span className="text-gray-300 text-sm">Cards per row:</span>
              <select
                value={content.columns || 3}
                onChange={(e) => updateContent('columns', parseInt(e.target.value))}
                className="bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
              >
                <option value={2}>2 columns</option>
                <option value={3}>3 columns</option>
              </select>
            </div>

            {/* Testimonials Editor */}
            <div className="border border-gray-600 rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center">
                <h4 className="text-white font-medium text-sm">Testimonials (up to 6)</h4>
                {(content.testimonials || []).length < 6 && (
                  <button
                    type="button"
                    onClick={() => {
                      const testimonials = content.testimonials || [];
                      updateContent('testimonials', [...testimonials, { title: '', body: '', reader_name: '', reader_location: '' }]);
                    }}
                    className="text-xs bg-gray-600 hover:bg-gray-500 text-white px-3 py-1 rounded"
                  >
                    + Add Testimonial
                  </button>
                )}
              </div>
              
              {(content.testimonials || []).map((testimonial, index) => (
                <div key={index} className="bg-gray-700/50 rounded-lg p-4 space-y-3">
                  <div className="flex justify-between items-start">
                    <span className="text-gold-400 font-display text-sm">Testimonial {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => {
                        const testimonials = [...(content.testimonials || [])];
                        testimonials.splice(index, 1);
                        updateContent('testimonials', testimonials);
                      }}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      ✕ Remove
                    </button>
                  </div>
                  
                  {/* Title */}
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Title / Headline</label>
                    <input
                      type="text"
                      value={testimonial.title || ''}
                      onChange={(e) => {
                        const testimonials = [...(content.testimonials || [])];
                        testimonials[index] = { ...testimonials[index], title: e.target.value };
                        updateContent('testimonials', testimonials);
                      }}
                      className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                      placeholder="A truly immersive experience"
                    />
                  </div>
                  
                  {/* Body */}
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Testimonial Text</label>
                    <textarea
                      value={testimonial.body || ''}
                      onChange={(e) => {
                        const testimonials = [...(content.testimonials || [])];
                        testimonials[index] = { ...testimonials[index], body: e.target.value };
                        updateContent('testimonials', testimonials);
                      }}
                      className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                      placeholder="The testimonial text..."
                      rows={3}
                    />
                  </div>
                  
                  {/* Reader Info */}
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Reader Name (optional)</label>
                      <input
                        type="text"
                        value={testimonial.reader_name || ''}
                        onChange={(e) => {
                          const testimonials = [...(content.testimonials || [])];
                          testimonials[index] = { ...testimonials[index], reader_name: e.target.value };
                          updateContent('testimonials', testimonials);
                        }}
                        className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                        placeholder="E.M."
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Location (optional)</label>
                      <input
                        type="text"
                        value={testimonial.reader_location || ''}
                        onChange={(e) => {
                          const testimonials = [...(content.testimonials || [])];
                          testimonials[index] = { ...testimonials[index], reader_location: e.target.value };
                          updateContent('testimonials', testimonials);
                        }}
                        className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                        placeholder="Boston"
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Footer Text */}
            <div>
              <label className="block text-gray-300 text-sm mb-1">Footer Text (optional)</label>
              <input 
                type="text" 
                value={content.footer_text || ''} 
                onChange={(e) => updateContent('footer_text', e.target.value)} 
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" 
                placeholder="Early readers who experienced the first correspondences." 
              />
            </div>

            {/* Decorative Dividers Toggle */}
            <div className="flex items-center justify-between bg-gray-700/50 rounded px-3 py-2">
              <span className="text-gray-300 text-sm">Show Decorative Dividers (✧ ✧ ✧)</span>
              <button
                type="button"
                onClick={() => updateContent('show_dividers', !content.show_dividers)}
                className={`relative w-12 h-6 rounded-full transition-colors ${content.show_dividers !== false ? 'bg-gold-500' : 'bg-gray-600'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${content.show_dividers !== false ? 'left-7' : 'left-1'}`} />
              </button>
            </div>
          </>
        );
      case 'first_letter_preview':
        return (
          <>
            {/* Section Heading */}
            <div>
              <label className="block text-gray-300 text-sm mb-1">Section Heading</label>
              <input 
                type="text" 
                value={content.heading || ''} 
                onChange={(e) => updateContent('heading', e.target.value)} 
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" 
                placeholder="A Glimpse Into the Correspondence" 
              />
            </div>

            {/* Intro Text */}
            <div>
              <label className="block text-gray-300 text-sm mb-1">Introduction Text</label>
              <input 
                type="text" 
                value={content.intro_text || ''} 
                onChange={(e) => updateContent('intro_text', e.target.value)} 
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" 
                placeholder="Before you commit to this journey, perhaps a taste of what awaits..." 
              />
            </div>

            {/* Letter Excerpt */}
            <div>
              <label className="block text-gray-300 text-sm mb-1">Letter Excerpt (HTML)</label>
              <p className="text-gray-500 text-xs mb-2">
                Write the letter excerpt with paragraph tags. This should feel like an actual letter.
              </p>
              <textarea 
                value={content.letter_excerpt || ''} 
                onChange={(e) => updateContent('letter_excerpt', e.target.value)} 
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm font-mono" 
                placeholder="<p>My dearest friend,</p><p>I write to you now...</p>"
                rows={8}
              />
              {content.letter_excerpt && (
                <div className="mt-3 p-4 bg-gray-800 rounded border border-gray-700">
                  <p className="text-gray-400 text-xs mb-2">Preview:</p>
                  <div 
                    className="text-gray-200 text-sm prose prose-invert max-w-none font-serif"
                    dangerouslySetInnerHTML={{ __html: content.letter_excerpt }}
                  />
                </div>
              )}
            </div>

            {/* Character Name */}
            <div>
              <label className="block text-gray-300 text-sm mb-1">Character Name (optional)</label>
              <input 
                type="text" 
                value={content.character_name || ''} 
                onChange={(e) => updateContent('character_name', e.target.value)} 
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" 
                placeholder="Sienna Mae" 
              />
            </div>

            {/* Continuation Note */}
            <div>
              <label className="block text-gray-300 text-sm mb-1">Continuation Note</label>
              <input 
                type="text" 
                value={content.continuation_note || ''} 
                onChange={(e) => updateContent('continuation_note', e.target.value)} 
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" 
                placeholder="Subscribe to receive the full letter..." 
              />
            </div>

            {/* Show CTA Toggle */}
            <div className="flex items-center justify-between bg-gray-700/50 rounded px-3 py-2">
              <span className="text-gray-300 text-sm">Show Call-to-Action Button</span>
              <button
                type="button"
                onClick={() => updateContent('show_cta', !content.show_cta)}
                className={`relative w-12 h-6 rounded-full transition-colors ${content.show_cta ? 'bg-gold-500' : 'bg-gray-600'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${content.show_cta ? 'left-7' : 'left-1'}`} />
              </button>
            </div>

            {/* CTA Options */}
            {content.show_cta && (
              <div className="grid grid-cols-2 gap-4 p-3 bg-gray-700/30 rounded">
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Button Text</label>
                  <input
                    type="text"
                    value={content.cta_text || ''}
                    onChange={(e) => updateContent('cta_text', e.target.value)}
                    className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                    placeholder="Begin the Correspondence"
                  />
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Button Link</label>
                  <input
                    type="text"
                    value={content.cta_link || ''}
                    onChange={(e) => updateContent('cta_link', e.target.value)}
                    className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                    placeholder="/membership"
                  />
                </div>
              </div>
            )}

            {/* Decorative Dividers Toggle */}
            <div className="flex items-center justify-between bg-gray-700/50 rounded px-3 py-2">
              <span className="text-gray-300 text-sm">Show Decorative Dividers (✧ ✧ ✧)</span>
              <button
                type="button"
                onClick={() => updateContent('show_dividers', !content.show_dividers)}
                className={`relative w-12 h-6 rounded-full transition-colors ${content.show_dividers !== false ? 'bg-gold-500' : 'bg-gray-600'}`}
              >
                <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${content.show_dividers !== false ? 'left-7' : 'left-1'}`} />
              </button>
            </div>
          </>
        );
      case 'scrolling_marquee':
        return (
          <>
            <div>
              <label className="block text-gray-300 text-sm mb-1">Banner Text</label>
              <textarea
                value={content.text || ''}
                onChange={(e) => updateContent('text', e.target.value)}
                className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm"
                rows={3}
                placeholder="Unplug • Slow Down • Open An Envelope •"
              />
              <p className="text-gray-500 text-xs mt-1">Use • or other separators between phrases</p>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-1">Background Colour</label>
                <div className="flex gap-2">
                  <input type="color" value={content.bg_color || '#17352D'} onChange={(e) => updateContent('bg_color', e.target.value)} className="w-10 h-9 rounded border border-gray-600 cursor-pointer" />
                  <input type="text" value={content.bg_color || '#17352D'} onChange={(e) => updateContent('bg_color', e.target.value)} className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
                </div>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1">Text Colour</label>
                <div className="flex gap-2">
                  <input type="color" value={content.text_color || '#C7A15A'} onChange={(e) => updateContent('text_color', e.target.value)} className="w-10 h-9 rounded border border-gray-600 cursor-pointer" />
                  <input type="text" value={content.text_color || '#C7A15A'} onChange={(e) => updateContent('text_color', e.target.value)} className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
                </div>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-1">Scroll Speed ({content.speed || 30}s)</label>
                <input type="range" min="10" max="80" value={content.speed || 30} onChange={(e) => updateContent('speed', parseInt(e.target.value))} className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer" />
                <div className="flex justify-between text-gray-500 text-xs mt-1"><span>Fast</span><span>Slow</span></div>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1">Font Size ({content.font_size || 18}px)</label>
                <input type="range" min="12" max="32" value={content.font_size || 18} onChange={(e) => updateContent('font_size', parseInt(e.target.value))} className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer" />
              </div>
              <div className="flex items-end pb-1">
                <label className="flex items-center gap-2 text-gray-300 text-sm cursor-pointer">
                  <input type="checkbox" checked={content.glow !== false} onChange={(e) => updateContent('glow', e.target.checked)} className="rounded bg-gray-700" />
                  Gold Glow
                </label>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-1">Top Spacing ({content.spacing_top ?? 0}px)</label>
                <input type="range" min="0" max="60" value={content.spacing_top ?? 0} onChange={(e) => updateContent('spacing_top', parseInt(e.target.value))} className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer" />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1">Bottom Spacing ({content.spacing_bottom ?? 0}px)</label>
                <input type="range" min="0" max="60" value={content.spacing_bottom ?? 0} onChange={(e) => updateContent('spacing_bottom', parseInt(e.target.value))} className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer" />
              </div>
            </div>
            {/* Live Preview */}
            <div className="mt-2 rounded overflow-hidden">
              <div style={{ backgroundColor: content.bg_color || '#17352D', padding: '12px 0', overflow: 'hidden' }}>
                <p style={{ color: content.text_color || '#C7A15A', fontFamily: 'Georgia, serif', fontSize: `${(content.font_size || 18) * 0.7}px`, letterSpacing: '0.12em', textAlign: 'center', textShadow: content.glow !== false ? `0 0 8px ${content.text_color || '#C7A15A'}66` : 'none' }}>
                  {(content.text || 'Preview text...').substring(0, 60)}...
                </p>
              </div>
            </div>
          </>
        );
      case 'feature_spotlight':
        return (
          <>
            {/* Heading */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-1">Subtitle</label>
                <input type="text" value={content.subtitle || ''} onChange={(e) => updateContent('subtitle', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" placeholder="SPOTLIGHT" />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1">Main Title</label>
                <input type="text" value={content.title || ''} onChange={(e) => updateContent('title', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" />
              </div>
            </div>

            {/* Image */}
            <FileUpload uploadType="media" onUpload={(url) => updateContent('image_url', url)} currentUrl={content.image_url} label="Featured Image" accept="image/*" />
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-gray-400 text-xs mb-1">Image Alt Text</label>
                <input type="text" value={content.image_alt || ''} onChange={(e) => updateContent('image_alt', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs" />
              </div>
              <div>
                <label className="block text-gray-400 text-xs mb-1">Image Caption</label>
                <input type="text" value={content.image_caption || ''} onChange={(e) => updateContent('image_caption', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs" />
              </div>
              <div>
                <label className="block text-gray-400 text-xs mb-1">Corner Style</label>
                <select value={content.image_corner || 'square'} onChange={(e) => updateContent('image_corner', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs">
                  <option value="square">Square</option>
                  <option value="rounded">Rounded</option>
                  <option value="vintage">Vintage</option>
                </select>
              </div>
            </div>

            {/* Description */}
            <div>
              <label className="block text-gray-300 text-sm mb-1">Description (HTML)</label>
              <textarea value={content.description || ''} onChange={(e) => updateContent('description', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm" rows={4} />
            </div>

            {/* CTA */}
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-gray-400 text-xs mb-1">Button Text</label>
                <input type="text" value={content.button_text || ''} onChange={(e) => updateContent('button_text', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs" />
              </div>
              <div>
                <label className="block text-gray-400 text-xs mb-1">Button Link</label>
                <input type="text" value={content.button_link || ''} onChange={(e) => updateContent('button_link', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs" placeholder="/membership" />
              </div>
              <div>
                <label className="block text-gray-400 text-xs mb-1">Button Style</label>
                <select value={content.button_style || 'outline'} onChange={(e) => updateContent('button_style', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs">
                  <option value="outline">Outline</option>
                  <option value="filled">Filled Gold</option>
                </select>
              </div>
            </div>

            {/* Layout & Style */}
            <div className="border border-gray-600 rounded-lg p-3 space-y-3">
              <h4 className="text-white font-medium text-sm">Layout & Background</h4>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Section Width</label>
                  <select value={content.section_width || 'default'} onChange={(e) => updateContent('section_width', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs">
                    <option value="narrow">Narrow</option>
                    <option value="default">Default</option>
                    <option value="wide">Wide</option>
                  </select>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Background Colour</label>
                  <div className="flex gap-1">
                    <input type="color" value={content.bg_color || '#FAF7F2'} onChange={(e) => updateContent('bg_color', e.target.value)} className="w-9 h-8 rounded border border-gray-600 cursor-pointer" />
                    <input type="text" value={content.bg_color || '#FAF7F2'} onChange={(e) => updateContent('bg_color', e.target.value)} className="flex-1 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs" />
                  </div>
                </div>
                <div>
                  <label className="block text-gray-400 text-xs mb-1">Top/Bottom Spacing</label>
                  <div className="flex gap-1">
                    <input type="number" value={content.spacing_top ?? 48} onChange={(e) => updateContent('spacing_top', parseInt(e.target.value) || 0)} className="w-1/2 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs" placeholder="Top" />
                    <input type="number" value={content.spacing_bottom ?? 48} onChange={(e) => updateContent('spacing_bottom', parseInt(e.target.value) || 0)} className="w-1/2 bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-xs" placeholder="Bottom" />
                  </div>
                </div>
              </div>
              <div className="flex gap-4">
                <label className="flex items-center gap-2 text-gray-300 text-xs cursor-pointer">
                  <input type="checkbox" checked={content.show_frame === true} onChange={(e) => updateContent('show_frame', e.target.checked)} className="rounded bg-gray-700" />
                  Vintage Frame
                </label>
                <label className="flex items-center gap-2 text-gray-300 text-xs cursor-pointer">
                  <input type="checkbox" checked={content.show_divider !== false} onChange={(e) => updateContent('show_divider', e.target.checked)} className="rounded bg-gray-700" />
                  Decorative Dividers
                </label>
              </div>
              <label className="flex items-center gap-2 text-gray-300 text-xs cursor-pointer">
                <input type="checkbox" checked={content.bg_image_enabled === true} onChange={(e) => updateContent('bg_image_enabled', e.target.checked)} className="rounded bg-gray-700" />
                Background Image
              </label>
              {content.bg_image_enabled && (
                <>
                  <FileUpload uploadType="media" onUpload={(url) => updateContent('bg_image_url', url)} currentUrl={content.bg_image_url} label="Background Image" accept="image/*" />
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Opacity ({content.bg_image_opacity ?? 10}%)</label>
                    <input type="range" min="5" max="100" value={content.bg_image_opacity ?? 10} onChange={(e) => updateContent('bg_image_opacity', parseInt(e.target.value))} className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer" />
                  </div>
                </>
              )}
            </div>
          </>
        );
      case 'editorial_story':
        return (
          <>
            {/* Global Settings */}
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-1">Default Layout</label>
                <select value={content.layout || 'alternate'} onChange={(e) => updateContent('layout', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm">
                  <option value="alternate">Alternate (zig-zag)</option>
                  <option value="image_left">Image Left</option>
                  <option value="image_right">Image Right</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1">Vertical Align</label>
                <select value={content.vertical_align || 'center'} onChange={(e) => updateContent('vertical_align', e.target.value)} className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white text-sm">
                  <option value="top">Top</option>
                  <option value="center">Center</option>
                  <option value="bottom">Bottom</option>
                </select>
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1">Background</label>
                <div className="flex gap-2">
                  <input type="color" value={content.bg_color === 'transparent' ? '#FAF7F2' : (content.bg_color || '#FAF7F2')} onChange={(e) => updateContent('bg_color', e.target.value)} className="w-10 h-9 rounded border border-gray-600 cursor-pointer" />
                  <button onClick={() => updateContent('bg_color', 'transparent')} className={`text-xs px-2 rounded ${content.bg_color === 'transparent' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300'}`}>Default</button>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-gray-300 text-sm mb-1">Top Spacing ({content.spacing_top ?? 40}px)</label>
                <input type="range" min="0" max="100" value={content.spacing_top ?? 40} onChange={(e) => updateContent('spacing_top', parseInt(e.target.value))} className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer" />
              </div>
              <div>
                <label className="block text-gray-300 text-sm mb-1">Bottom Spacing ({content.spacing_bottom ?? 40}px)</label>
                <input type="range" min="0" max="100" value={content.spacing_bottom ?? 40} onChange={(e) => updateContent('spacing_bottom', parseInt(e.target.value))} className="w-full h-2 bg-gray-600 rounded-lg appearance-none cursor-pointer" />
              </div>
            </div>
            <label className="flex items-center gap-2 text-gray-300 text-sm cursor-pointer">
              <input type="checkbox" checked={content.show_frame === true} onChange={(e) => updateContent('show_frame', e.target.checked)} className="rounded bg-gray-700" />
              Decorative Image Frame
            </label>

            {/* Story Blocks */}
            <div className="border-t border-gray-700 pt-4 mt-2">
              <div className="flex justify-between items-center mb-3">
                <h4 className="text-white font-medium">Story Blocks ({(content.blocks || []).length})</h4>
                <button
                  type="button"
                  onClick={() => {
                    const blocks = [...(content.blocks || [])];
                    blocks.push({ image_url: '', image_caption: '', image_position: 'center', small_heading: '', main_heading: '', narrative: '', button_text: '', button_link: '', layout: 'image_left' });
                    updateContent('blocks', blocks);
                  }}
                  className="text-xs bg-green-700 hover:bg-green-600 text-white px-3 py-1.5 rounded"
                >
                  + Add Block
                </button>
              </div>
              {(content.blocks || []).map((block, idx) => (
                <div key={idx} className="bg-gray-750 border border-gray-600 rounded-lg p-4 mb-4 space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-300 text-sm font-medium">Block {idx + 1}</span>
                    <div className="flex gap-2">
                      {idx > 0 && <button type="button" onClick={() => { const b = [...content.blocks]; [b[idx-1], b[idx]] = [b[idx], b[idx-1]]; updateContent('blocks', b); }} className="text-xs text-gray-400 hover:text-white">Move Up</button>}
                      {idx < (content.blocks || []).length - 1 && <button type="button" onClick={() => { const b = [...content.blocks]; [b[idx], b[idx+1]] = [b[idx+1], b[idx]]; updateContent('blocks', b); }} className="text-xs text-gray-400 hover:text-white">Move Down</button>}
                      <button type="button" onClick={() => { const b = [...content.blocks]; b.push({...block}); updateContent('blocks', b); }} className="text-xs text-blue-400 hover:text-blue-300">Duplicate</button>
                      <button type="button" onClick={() => { if (window.confirm('Remove this block?')) { const b = [...content.blocks]; b.splice(idx, 1); updateContent('blocks', b); }}} className="text-xs text-red-400 hover:text-red-300">Remove</button>
                    </div>
                  </div>
                  <FileUpload uploadType="media" onUpload={(url) => { const b = [...content.blocks]; b[idx] = {...b[idx], image_url: url}; updateContent('blocks', b); }} currentUrl={block.image_url} label="Image" accept="image/*" />
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Image Position</label>
                      <select value={block.image_position || 'center'} onChange={(e) => { const b = [...content.blocks]; b[idx] = {...b[idx], image_position: e.target.value}; updateContent('blocks', b); }} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs">
                        <option value="center">Center</option>
                        <option value="top">Top</option>
                        <option value="bottom">Bottom</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Image Caption</label>
                      <input type="text" value={block.image_caption || ''} onChange={(e) => { const b = [...content.blocks]; b[idx] = {...b[idx], image_caption: e.target.value}; updateContent('blocks', b); }} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Small Heading</label>
                    <input type="text" value={block.small_heading || ''} onChange={(e) => { const b = [...content.blocks]; b[idx] = {...b[idx], small_heading: e.target.value}; updateContent('blocks', b); }} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm" placeholder="THE STORY" />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Main Heading</label>
                    <input type="text" value={block.main_heading || ''} onChange={(e) => { const b = [...content.blocks]; b[idx] = {...b[idx], main_heading: e.target.value}; updateContent('blocks', b); }} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm" placeholder="Letters From Another World" />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-xs mb-1">Narrative (HTML)</label>
                    <textarea value={block.narrative || ''} onChange={(e) => { const b = [...content.blocks]; b[idx] = {...b[idx], narrative: e.target.value}; updateContent('blocks', b); }} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-sm h-20" placeholder="<p>Your narrative text...</p>" />
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Button Text</label>
                      <input type="text" value={block.button_text || ''} onChange={(e) => { const b = [...content.blocks]; b[idx] = {...b[idx], button_text: e.target.value}; updateContent('blocks', b); }} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Button Link</label>
                      <input type="text" value={block.button_link || ''} onChange={(e) => { const b = [...content.blocks]; b[idx] = {...b[idx], button_link: e.target.value}; updateContent('blocks', b); }} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs" placeholder="/membership" />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Block Layout</label>
                      <select value={block.layout || 'image_left'} onChange={(e) => { const b = [...content.blocks]; b[idx] = {...b[idx], layout: e.target.value}; updateContent('blocks', b); }} className="w-full bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs">
                        <option value="image_left">Image Left</option>
                        <option value="image_right">Image Right</option>
                      </select>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </>
        );
      default:
        return null;
    }
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Homepage Builder</h2>
          <p className="text-gray-400 text-sm mt-1">Drag sections to reorder. Only published sections appear on the live site.</p>
        </div>
        <button 
          onClick={() => { resetForm(); setShowAddModal(true); }} 
          className="bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded"
        >
          + Add Section
        </button>
      </div>

      {/* Sections List */}
      <div className="space-y-3">
        {sections.map((section, index) => (
          <div key={section.id} className={`bg-gray-800 rounded-lg p-4 border-l-4 ${section.is_published ? 'border-green-500' : 'border-gray-600'}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="flex flex-col gap-1">
                  <button onClick={() => handleMoveUp(index)} disabled={index === 0} className="text-gray-400 hover:text-white disabled:opacity-30 text-xs">▲</button>
                  <button onClick={() => handleMoveDown(index)} disabled={index === sections.length - 1} className="text-gray-400 hover:text-white disabled:opacity-30 text-xs">▼</button>
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-300">{section.section_type}</span>
                    <h4 className="text-white font-medium">{section.title || sectionTypes.find(t => t.type === section.section_type)?.label}</h4>
                  </div>
                  <p className="text-gray-500 text-sm">{sectionTypes.find(t => t.type === section.section_type)?.description}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => handleTogglePublish(section)} 
                  className={`px-3 py-1 rounded text-sm ${section.is_published ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-300'}`}
                >
                  {section.is_published ? 'Published' : 'Draft'}
                </button>
                <button onClick={() => handleEdit(section)} className="text-amber-400 hover:text-amber-300 px-3 py-1">Edit</button>
                <button onClick={() => handleDelete(section.id)} className="text-red-400 hover:text-red-300 px-3 py-1">Delete</button>
              </div>
            </div>
          </div>
        ))}
        {sections.length === 0 && (
          <p className="text-gray-500 text-center py-8">No sections yet. Click "Add Section" to create your first homepage section.</p>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto py-8">
          <div className={`bg-gray-800 rounded-lg w-full mx-4 ${
            formData.section_type === 'hero' 
              ? 'max-w-[95vw] max-h-[94vh] flex flex-col p-4' 
              : 'max-w-2xl max-h-[90vh] overflow-y-auto p-6'
          }`}>
            <h3 className="text-lg font-semibold text-white mb-4">{editingId ? 'Edit Section' : 'Add New Section'}</h3>
            <form onSubmit={handleSubmit} className={`space-y-4 ${formData.section_type === 'hero' ? 'flex flex-col flex-1 min-h-0' : ''}`}>
              <div className={formData.section_type === 'hero' ? 'flex gap-4 items-end flex-shrink-0' : ''}>
                <div className={formData.section_type === 'hero' ? 'flex-1' : ''}>
                  <label className="block text-gray-300 text-sm mb-1">Section Type</label>
                  <select 
                    value={formData.section_type} 
                    onChange={(e) => setFormData({ ...formData, section_type: e.target.value, content: getDefaultContent(e.target.value) })}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    disabled={editingId}
                  >
                    {sectionTypes.map(t => (
                      <option key={t.type} value={t.type}>{t.label} - {t.description}</option>
                    ))}
                  </select>
                </div>
                <div className={formData.section_type === 'hero' ? 'flex-1' : 'mt-4'}>
                  <label className="block text-gray-300 text-sm mb-1">Section Title (Admin reference)</label>
                  <input 
                    type="text" 
                    value={formData.title} 
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    placeholder="e.g., Main Hero, Welcome Quote"
                  />
                </div>
              </div>
              
              <div className={`border-t border-gray-700 pt-4 ${formData.section_type === 'hero' ? 'flex-1 min-h-0 overflow-hidden' : ''}`}>
                {formData.section_type !== 'hero' && <h4 className="text-white font-medium mb-3">Section Content</h4>}
                {renderContentFields()}
              </div>

              <div className={`flex items-center gap-4 ${formData.section_type === 'hero' ? 'flex-shrink-0 pt-2' : ''}`}>
                <div className="flex items-center gap-2">
                  <input 
                    type="checkbox" 
                    checked={formData.is_published} 
                    onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
                    className="rounded bg-gray-700"
                    id="is_published"
                  />
                  <label htmlFor="is_published" className="text-gray-300 text-sm">Publish immediately</label>
                </div>
                <div className="flex gap-3 ml-auto">
                  <button type="submit" className="bg-amber-600 hover:bg-amber-500 text-white px-6 py-2 rounded" data-testid="save-section-btn">
                    {editingId ? 'Update Section' : 'Add Section'}
                  </button>
                  <button type="button" onClick={() => { setShowAddModal(false); resetForm(); }} className="bg-gray-600 hover:bg-gray-500 text-white px-6 py-2 rounded">
                    Cancel
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== CHARACTERS TAB ==============
const CharactersTab = ({ getAuthHeaders }) => {
  const [characters, setCharacters] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '', slug: '', era: '', short_bio: '', long_backstory: '', self_intro: '',
    gallery_images: [], status: 'active', is_published: true, display_order: 0,
    portrait_url: '', membership_ids: []
  });
  const [newGalleryUrl, setNewGalleryUrl] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [charsRes, membRes] = await Promise.all([
        axios.get(`${API}/admin/characters`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/memberships`, { headers: getAuthHeaders() })
      ]);
      setCharacters(charsRes.data);
      setMemberships(membRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/characters/${editingId}`, formData, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/admin/characters`, formData, { headers: getAuthHeaders() });
      }
      fetchData();
      resetForm();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to save character: ${errorMsg}`);
    }
  };

  const handleEdit = (char) => {
    setEditingId(char.id);
    setFormData({
      name: char.name, slug: char.slug || '', era: char.era || '',
      short_bio: char.short_bio || '', long_backstory: char.long_backstory || '',
      self_intro: char.self_intro || '', gallery_images: char.gallery_images || [],
      status: char.status, is_published: char.is_published, display_order: char.display_order,
      portrait_url: char.portrait_url || '', membership_ids: char.membership_ids || []
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this character?')) return;
    await axios.delete(`${API}/admin/characters/${id}`, { headers: getAuthHeaders() });
    fetchData();
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: '', slug: '', era: '', short_bio: '', long_backstory: '', self_intro: '',
      gallery_images: [], status: 'active', is_published: true, display_order: 0,
      portrait_url: '', membership_ids: []
    });
    setNewGalleryUrl('');
  };

  const generateSlug = (name) => {
    return name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  };

  const addGalleryImage = (url) => {
    if (url && !formData.gallery_images.includes(url)) {
      setFormData({ ...formData, gallery_images: [...formData.gallery_images, url] });
    }
    setNewGalleryUrl('');
  };

  const removeGalleryImage = (index) => {
    const updated = formData.gallery_images.filter((_, i) => i !== index);
    setFormData({ ...formData, gallery_images: updated });
  };

  const moveGalleryImage = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= formData.gallery_images.length) return;
    const updated = [...formData.gallery_images];
    [updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
    setFormData({ ...formData, gallery_images: updated });
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-white">Characters</h2>

      <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold text-white">{editingId ? 'Edit Character' : 'Add Character'}</h3>

        {/* Row 1: Name, Slug, Era */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-300 text-sm mb-2">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => {
                const name = e.target.value;
                const updates = { name };
                if (!editingId && !formData.slug) {
                  updates.slug = generateSlug(name);
                }
                setFormData({ ...formData, ...updates });
              }}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white focus:outline-none focus:border-amber-500"
              required
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Slug (URL)</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: generateSlug(e.target.value) })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
              placeholder="auto-generated-from-name"
            />
            {formData.slug && (
              <p className="text-gray-500 text-xs mt-1">/characters/{formData.slug}</p>
            )}
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Era</label>
            <input
              type="text"
              value={formData.era}
              onChange={(e) => setFormData({ ...formData, era: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
              placeholder="e.g. Victorian England, 1888"
            />
          </div>
        </div>

        {/* Row 2: Status, Display Order */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-300 text-sm mb-2">Status</label>
            <select
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            >
              <option value="active">Active</option>
              <option value="coming_soon">Coming Soon</option>
              <option value="retired">Retired</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Display Order</label>
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            />
          </div>
        </div>

        {/* Short Bio */}
        <div>
          <label className="block text-gray-300 text-sm mb-2">Short Bio (Teaser for index page)</label>
          <textarea
            value={formData.short_bio}
            onChange={(e) => setFormData({ ...formData, short_bio: e.target.value })}
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white h-20"
            placeholder="Brief description shown on character cards..."
          />
        </div>

        {/* Long Backstory */}
        <div>
          <label className="block text-gray-300 text-sm mb-2">Long Backstory (Rich Text HTML)</label>
          <textarea
            value={formData.long_backstory}
            onChange={(e) => setFormData({ ...formData, long_backstory: e.target.value })}
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white h-32"
            placeholder="<p>Character backstory...</p>"
          />
        </div>

        {/* Self Introduction - "A Letter From Me" */}
        <div>
          <label className="block text-gray-300 text-sm mb-2">Self Introduction (First-person "A Letter From Me")</label>
          <textarea
            value={formData.self_intro}
            onChange={(e) => setFormData({ ...formData, self_intro: e.target.value })}
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white h-32"
            placeholder="Write in first person as the character... e.g. 'Dear reader, I have awaited your arrival...'"
          />
        </div>

        {/* Portrait and Memberships */}
        <div className="grid grid-cols-2 gap-4">
          <FileUpload
            uploadType="portraits"
            onUpload={(url) => setFormData({ ...formData, portrait_url: url })}
            currentUrl={formData.portrait_url}
            label="Portrait Image"
            accept="image/*"
          />
          <div>
            <label className="block text-gray-300 text-sm mb-2">Memberships</label>
            <div className="space-y-2 max-h-32 overflow-y-auto">
              {memberships.map((m) => (
                <label key={m.id} className="flex items-center gap-2 text-gray-300 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.membership_ids.includes(m.id)}
                    onChange={(e) => {
                      const ids = e.target.checked
                        ? [...formData.membership_ids, m.id]
                        : formData.membership_ids.filter(id => id !== m.id);
                      setFormData({ ...formData, membership_ids: ids });
                    }}
                    className="rounded bg-gray-700"
                  />
                  {m.name}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Gallery Images Section */}
        <div>
          <label className="block text-gray-300 text-sm mb-2">Gallery Images ("What You'll Receive")</label>
          <div className="bg-gray-700 rounded p-4 space-y-3">
            {/* Existing gallery images */}
            {formData.gallery_images.length > 0 && (
              <div className="grid grid-cols-4 gap-3">
                {formData.gallery_images.map((url, index) => (
                  <div key={index} className="relative group">
                    <img
                      src={url}
                      alt={`Gallery ${index + 1}`}
                      className="w-full h-24 object-cover rounded"
                    />
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-1">
                      <button
                        type="button"
                        onClick={() => moveGalleryImage(index, -1)}
                        disabled={index === 0}
                        className="p-1 bg-gray-600 rounded text-white text-xs disabled:opacity-30"
                      >
                        ←
                      </button>
                      <button
                        type="button"
                        onClick={() => removeGalleryImage(index)}
                        className="p-1 bg-red-600 rounded text-white text-xs"
                      >
                        ✕
                      </button>
                      <button
                        type="button"
                        onClick={() => moveGalleryImage(index, 1)}
                        disabled={index === formData.gallery_images.length - 1}
                        className="p-1 bg-gray-600 rounded text-white text-xs disabled:opacity-30"
                      >
                        →
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
            {/* Add new gallery image */}
            <div className="flex gap-2">
              <FileUpload
                uploadType="gallery"
                onUpload={(url) => addGalleryImage(url)}
                label=""
                accept="image/*"
                compact={true}
              />
              <span className="text-gray-400 text-sm self-center">or</span>
              <input
                type="text"
                value={newGalleryUrl}
                onChange={(e) => setNewGalleryUrl(e.target.value)}
                placeholder="Paste image URL..."
                className="flex-1 bg-gray-600 border border-gray-500 rounded px-3 py-2 text-white text-sm"
              />
              <button
                type="button"
                onClick={() => addGalleryImage(newGalleryUrl)}
                className="bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded text-sm"
              >
                Add
              </button>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-4">
          <label className="flex items-center gap-2 text-gray-300">
            <input
              type="checkbox"
              checked={formData.is_published}
              onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })}
              className="rounded bg-gray-700"
            />
            Published
          </label>
        </div>

        <div className="flex gap-4">
          <button type="submit" className="bg-amber-600 hover:bg-amber-500 text-white px-6 py-2 rounded">
            {editingId ? 'Update' : 'Create'}
          </button>
          {editingId && (
            <button type="button" onClick={resetForm} className="bg-gray-600 hover:bg-gray-500 text-white px-6 py-2 rounded">
              Cancel
            </button>
          )}
        </div>
      </form>

      <div className="space-y-4">
        {characters.map((char) => (
          <div key={char.id} className="bg-gray-800 rounded-lg p-4 flex items-center gap-4">
            {char.portrait_url && (
              <img
                src={char.portrait_url}
                alt={char.name}
                className="w-16 h-16 object-cover rounded"
              />
            )}
            <div className="flex-1">
              <h4 className="text-white font-medium">{char.name}</h4>
              <p className="text-gray-400 text-sm">{char.short_bio || 'No bio'}</p>
              {char.slug && (
                <p className="text-gray-500 text-xs mt-1">/characters/{char.slug}</p>
              )}
              <div className="flex gap-2 mt-1">
                <span className={`text-xs px-2 py-1 rounded ${
                  char.status === 'active' ? 'bg-green-900 text-green-300' :
                  char.status === 'coming_soon' ? 'bg-yellow-900 text-yellow-300' :
                  'bg-gray-700 text-gray-300'
                }`}>
                  {char.status}
                </span>
                <span className={`text-xs px-2 py-1 rounded ${char.is_published ? 'bg-blue-900 text-blue-300' : 'bg-gray-700 text-gray-300'}`}>
                  {char.is_published ? 'Published' : 'Draft'}
                </span>
                {char.era && (
                  <span className="text-xs px-2 py-1 rounded bg-purple-900 text-purple-300">
                    {char.era}
                  </span>
                )}
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => handleEdit(char)} className="text-amber-400 hover:text-amber-300 px-3 py-1">Edit</button>
              <button onClick={() => handleDelete(char.id)} className="text-red-400 hover:text-red-300 px-3 py-1">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============== SCHEDULED LETTERS TAB ==============
const LettersTab = ({ getAuthHeaders }) => {
  const [letters, setLetters] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [memberships, setMemberships] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [testEmailModal, setTestEmailModal] = useState({ open: false, letterId: null, letterTitle: '' });
  const [testEmail, setTestEmail] = useState('');
  const [sendingTest, setSendingTest] = useState(false);
  const [formData, setFormData] = useState({
    character_id: '', title: '', subject_line: '', content_html: '',
    file_url: '', ink_saver_url: '', delay_days: 0, sequence_order: 0, is_active: true, membership_tier_ids: []
  });

  const fetchData = useCallback(async () => {
    try {
      const [lettersRes, charsRes, membersRes] = await Promise.all([
        axios.get(`${API}/admin/scheduled-letters`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/characters`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/memberships`, { headers: getAuthHeaders() })
      ]);
      setLetters(lettersRes.data);
      setCharacters(charsRes.data);
      setMemberships(membersRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/scheduled-letters/${editingId}`, formData, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/admin/scheduled-letters`, formData, { headers: getAuthHeaders() });
      }
      fetchData();
      resetForm();
    } catch (error) {
      alert('Failed to save letter');
    }
  };

  const handleEdit = (letter) => {
    setEditingId(letter.id);
    setFormData({
      character_id: letter.character_id, title: letter.title, subject_line: letter.subject_line || '',
      content_html: letter.content_html || '', file_url: letter.file_url || '',
      ink_saver_url: letter.ink_saver_url || '',
      delay_days: letter.delay_days, sequence_order: letter.sequence_order, is_active: letter.is_active,
      membership_tier_ids: letter.membership_tier_ids || []
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this letter?')) return;
    await axios.delete(`${API}/admin/scheduled-letters/${id}`, { headers: getAuthHeaders() });
    fetchData();
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      character_id: '', title: '', subject_line: '', content_html: '',
      file_url: '', ink_saver_url: '', delay_days: 0, sequence_order: 0, is_active: true, membership_tier_ids: []
    });
  };

  const handleSendTest = async () => {
    if (!testEmail || !testEmailModal.letterId) return;
    setSendingTest(true);
    try {
      const res = await axios.post(
        `${API}/admin/scheduled-letters/${testEmailModal.letterId}/send-test?to_email=${encodeURIComponent(testEmail)}`,
        {},
        { headers: getAuthHeaders() }
      );
      if (res.data.success) {
        alert(`Test email sent to ${testEmail}!`);
      } else {
        alert(`Failed: ${res.data.message}`);
      }
      setTestEmailModal({ open: false, letterId: null, letterTitle: '' });
      setTestEmail('');
    } catch (error) {
      alert('Failed to send test email');
    }
    setSendingTest(false);
  };

  const getCharacterName = (id) => characters.find(c => c.id === id)?.name || 'Unknown';
  const getMembershipNames = (ids) => {
    if (!ids || ids.length === 0) return 'All tiers';
    return ids.map(id => memberships.find(m => m.id === id)?.name || id).join(', ');
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Scheduled Letters</h2>
        <button
          onClick={async () => {
            try {
              const res = await axios.post(`${API}/admin/trigger-letter-delivery`, {}, { headers: getAuthHeaders() });
              alert(`Delivered ${res.data.delivered_count} letters`);
            } catch (error) {
              alert('Failed to trigger delivery');
            }
          }}
          className="bg-green-600 hover:bg-green-500 text-white px-4 py-2 rounded text-sm"
        >
          Trigger Delivery Now
        </button>
      </div>

      <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold text-white">{editingId ? 'Edit Letter' : 'Add Scheduled Letter'}</h3>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-300 text-sm mb-2">Character *</label>
            <select
              value={formData.character_id}
              onChange={(e) => setFormData({ ...formData, character_id: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
              required
            >
              <option value="">Select character</option>
              {characters.map((c) => (
                <option key={c.id} value={c.id}>{c.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
              required
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Email Subject Line</label>
            <input
              type="text"
              value={formData.subject_line}
              onChange={(e) => setFormData({ ...formData, subject_line: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            />
          </div>
        </div>

        <div>
          <label className="block text-gray-300 text-sm mb-2">Letter Content (HTML)</label>
          <textarea
            value={formData.content_html}
            onChange={(e) => setFormData({ ...formData, content_html: e.target.value })}
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white h-40"
            placeholder="<p>Dear Reader...</p>"
          />
        </div>

        <div>
          <label className="block text-gray-300 text-sm mb-2">Membership Tier Gating (leave empty for all tiers)</label>
          <div className="flex flex-wrap gap-2 bg-gray-700 border border-gray-600 rounded p-3">
            {memberships.map((tier) => (
              <label key={tier.id} className="flex items-center gap-2 text-gray-300 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.membership_tier_ids.includes(tier.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setFormData({ ...formData, membership_tier_ids: [...formData.membership_tier_ids, tier.id] });
                    } else {
                      setFormData({ ...formData, membership_tier_ids: formData.membership_tier_ids.filter(id => id !== tier.id) });
                    }
                  }}
                  className="rounded bg-gray-600"
                />
                {tier.name}
              </label>
            ))}
            {memberships.length === 0 && <span className="text-gray-500 text-sm">No membership tiers available</span>}
          </div>
          <p className="text-gray-500 text-xs mt-1">If none selected, letter will be sent to ALL membership tiers</p>
        </div>

        <div className="grid grid-cols-4 gap-4">
          <div>
            <label className="block text-gray-300 text-sm mb-2">Delay (days after signup)</label>
            <input
              type="number"
              value={formData.delay_days}
              onChange={(e) => setFormData({ ...formData, delay_days: parseInt(e.target.value) || 0 })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
              min="0"
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Sequence Order</label>
            <input
              type="number"
              value={formData.sequence_order}
              onChange={(e) => setFormData({ ...formData, sequence_order: parseInt(e.target.value) || 0 })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            />
          </div>
          <FileUpload
            uploadType="letters"
            onUpload={(url) => setFormData({ ...formData, file_url: url })}
            currentUrl={formData.file_url}
            label="PDF Attachment (upload)"
            accept=".pdf"
          />
          <div>
            <label className="block text-gray-300 text-sm mb-2">Or External File URL</label>
            <input
              type="url"
              value={formData.file_url || ''}
              onChange={(e) => setFormData({ ...formData, file_url: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white text-sm"
              placeholder="https://r2.example.com/letter.pdf"
            />
            {formData.file_url && (
              <div className="flex items-center gap-2 mt-1">
                <a href={formData.file_url} target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline text-xs truncate max-w-[200px]">{formData.file_url}</a>
                <button type="button" onClick={() => setFormData({ ...formData, file_url: '' })} className="text-red-400 text-xs">Clear</button>
              </div>
            )}
            <p className="text-gray-500 text-xs mt-1">Paste a Cloudflare R2 or external URL for large PDFs</p>
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Ink Saver File URL</label>
            <input
              type="url"
              value={formData.ink_saver_url || ''}
              onChange={(e) => setFormData({ ...formData, ink_saver_url: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white text-sm"
              placeholder="https://r2.example.com/letter-ink-saver.pdf"
            />
            {formData.ink_saver_url && (
              <div className="flex items-center gap-2 mt-1">
                <a href={formData.ink_saver_url} target="_blank" rel="noopener noreferrer" className="text-amber-400 hover:underline text-xs truncate max-w-[200px]">{formData.ink_saver_url}</a>
                <button type="button" onClick={() => setFormData({ ...formData, ink_saver_url: '' })} className="text-red-400 text-xs">Clear</button>
              </div>
            )}
            <p className="text-gray-500 text-xs mt-1">Optional ink-saving version of the letter PDF</p>
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-gray-300">
              <input
                type="checkbox"
                checked={formData.is_active}
                onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                className="rounded bg-gray-700"
              />
              Active
            </label>
          </div>
        </div>

        <div className="flex gap-4">
          <button type="submit" className="bg-amber-600 hover:bg-amber-500 text-white px-6 py-2 rounded">
            {editingId ? 'Update' : 'Create'}
          </button>
          {editingId && (
            <button type="button" onClick={resetForm} className="bg-gray-600 hover:bg-gray-500 text-white px-6 py-2 rounded">
              Cancel
            </button>
          )}
        </div>
      </form>

      {/* Test Email Modal */}
      {testEmailModal.open && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
          <div className="bg-gray-800 rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-semibold text-white mb-4">Send Test Letter</h3>
            <p className="text-gray-400 text-sm mb-4">
              Send a test version of "{testEmailModal.letterTitle}" to:
            </p>
            <input
              type="email"
              value={testEmail}
              onChange={(e) => setTestEmail(e.target.value)}
              placeholder="Enter email address"
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white mb-4"
            />
            <p className="text-yellow-500 text-xs mb-4">
              Note: SendGrid is in placeholder mode. Email will only send if a valid API key is configured.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => { setTestEmailModal({ open: false, letterId: null, letterTitle: '' }); setTestEmail(''); }}
                className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded"
              >
                Cancel
              </button>
              <button
                onClick={handleSendTest}
                disabled={sendingTest || !testEmail}
                className="bg-blue-600 hover:bg-blue-500 disabled:bg-gray-600 text-white px-4 py-2 rounded"
              >
                {sendingTest ? 'Sending...' : 'Send Test'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-4">
        {letters.map((letter) => (
          <div key={letter.id} className="bg-gray-800 rounded-lg p-4 flex items-center gap-4">
            <div className="w-10 h-10 bg-gray-700 rounded flex items-center justify-center text-xl">✉️</div>
            <div className="flex-1">
              <h4 className="text-white font-medium">{letter.title}</h4>
              <p className="text-gray-400 text-sm">From: {getCharacterName(letter.character_id)}</p>
              <div className="flex flex-wrap gap-2 mt-1">
                <span className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-300">
                  Day {letter.delay_days}
                </span>
                <span className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-300">
                  Seq: {letter.sequence_order}
                </span>
                <span className={`text-xs px-2 py-1 rounded ${letter.is_active ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-300'}`}>
                  {letter.is_active ? 'Active' : 'Inactive'}
                </span>
                {letter.file_url && (
                  <span className="text-xs px-2 py-1 rounded bg-blue-900 text-blue-300">PDF</span>
                )}
                <span className="text-xs px-2 py-1 rounded bg-purple-900 text-purple-300">
                  {getMembershipNames(letter.membership_tier_ids)}
                </span>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setTestEmailModal({ open: true, letterId: letter.id, letterTitle: letter.title })}
                className="text-blue-400 hover:text-blue-300 px-3 py-1"
              >
                Test Send
              </button>
              <button onClick={() => handleEdit(letter)} className="text-amber-400 hover:text-amber-300 px-3 py-1">Edit</button>
              <button onClick={() => handleDelete(letter.id)} className="text-red-400 hover:text-red-300 px-3 py-1">Delete</button>
            </div>
          </div>
        ))}
        {letters.length === 0 && (
          <p className="text-gray-500 text-center py-8">No scheduled letters yet. Create one above.</p>
        )}
      </div>
    </div>
  );
};

// ============== PRODUCTS TAB ==============
const ProductsTab = ({ getAuthHeaders }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '', description: '', description_html: '', product_type: 'digital',
    price: 0, compare_at_price: null, sku: '', inventory_count: null,
    is_published: true, is_giftable: true, display_order: 0, image_url: '', file_url: ''
  });

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/products`, { headers: getAuthHeaders() });
      setProducts(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/products/${editingId}`, formData, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/admin/products`, formData, { headers: getAuthHeaders() });
      }
      fetchData();
      resetForm();
    } catch (error) {
      alert('Failed to save product');
    }
  };

  const handleEdit = (p) => {
    setEditingId(p.id);
    setFormData({
      title: p.title, description: p.description || '', description_html: p.description_html || '',
      product_type: p.product_type, price: p.price, compare_at_price: p.compare_at_price,
      sku: p.sku || '', inventory_count: p.inventory_count,
      is_published: p.is_published, is_giftable: p.is_giftable, display_order: p.display_order,
      image_url: p.image_url || '', file_url: p.file_url || ''
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await axios.delete(`${API}/admin/products/${id}`, { headers: getAuthHeaders() });
    fetchData();
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      title: '', description: '', description_html: '', product_type: 'digital',
      price: 0, compare_at_price: null, sku: '', inventory_count: null,
      is_published: true, is_giftable: true, display_order: 0, image_url: '', file_url: ''
    });
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-white">Shop Products</h2>

      <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold text-white">{editingId ? 'Edit Product' : 'Add Product'}</h3>

        <div className="grid grid-cols-4 gap-4">
          <div className="col-span-2">
            <label className="block text-gray-300 text-sm mb-2">Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
              required
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Type</label>
            <select
              value={formData.product_type}
              onChange={(e) => setFormData({ ...formData, product_type: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            >
              <option value="digital">Digital</option>
              <option value="physical">Physical</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Price ($)</label>
            <input
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            />
          </div>
        </div>

        <div>
          <label className="block text-gray-300 text-sm mb-2">Description</label>
          <textarea
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white h-24"
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <FileUpload
            uploadType="shop"
            onUpload={(url) => setFormData({ ...formData, image_url: url })}
            currentUrl={formData.image_url}
            label="Product Image"
            accept="image/*"
          />
          <FileUpload
            uploadType="shop"
            onUpload={(url) => setFormData({ ...formData, file_url: url })}
            currentUrl={formData.file_url}
            label="Digital File (for digital products)"
            accept="*"
          />
          <div>
            <label className="block text-gray-300 text-sm mb-2">SKU</label>
            <input
              type="text"
              value={formData.sku}
              onChange={(e) => setFormData({ ...formData, sku: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            />
            {formData.product_type === 'physical' && (
              <div className="mt-2">
                <label className="block text-gray-300 text-sm mb-2">Inventory Count</label>
                <input
                  type="number"
                  value={formData.inventory_count || ''}
                  onChange={(e) => setFormData({ ...formData, inventory_count: parseInt(e.target.value) || null })}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
                />
              </div>
            )}
          </div>
        </div>

        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-gray-300">
            <input type="checkbox" checked={formData.is_published} onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })} className="rounded bg-gray-700" />
            Published
          </label>
          <label className="flex items-center gap-2 text-gray-300">
            <input type="checkbox" checked={formData.is_giftable} onChange={(e) => setFormData({ ...formData, is_giftable: e.target.checked })} className="rounded bg-gray-700" />
            Giftable
          </label>
        </div>

        <div className="flex gap-4">
          <button type="submit" className="bg-amber-600 hover:bg-amber-500 text-white px-6 py-2 rounded">
            {editingId ? 'Update' : 'Create'}
          </button>
          {editingId && (
            <button type="button" onClick={resetForm} className="bg-gray-600 hover:bg-gray-500 text-white px-6 py-2 rounded">Cancel</button>
          )}
        </div>
      </form>

      <div className="grid grid-cols-2 gap-4">
        {products.map((p) => (
          <div key={p.id} className="bg-gray-800 rounded-lg p-4 flex gap-4">
            {p.image_url ? (
              <img src={p.image_url} alt={p.title} className="w-20 h-20 object-cover rounded" />
            ) : (
              <div className="w-20 h-20 bg-gray-700 rounded flex items-center justify-center text-2xl">🛍️</div>
            )}
            <div className="flex-1">
              <h4 className="text-white font-medium">{p.title}</h4>
              <p className="text-amber-400 font-semibold">${p.price.toFixed(2)}</p>
              <div className="flex gap-2 mt-1">
                <span className="text-xs px-2 py-1 rounded bg-gray-700 text-gray-300">{p.product_type}</span>
                <span className={`text-xs px-2 py-1 rounded ${p.is_published ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-300'}`}>
                  {p.is_published ? 'Published' : 'Draft'}
                </span>
              </div>
            </div>
            <div className="flex flex-col gap-1">
              <button onClick={() => handleEdit(p)} className="text-amber-400 hover:text-amber-300 text-sm">Edit</button>
              <button onClick={() => handleDelete(p.id)} className="text-red-400 hover:text-red-300 text-sm">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============== MEMBERSHIPS TAB ==============
const MembershipsTab = ({ getAuthHeaders }) => {
  const [tiers, setTiers] = useState([]);
  const [characters, setCharacters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [viewFilter, setViewFilter] = useState('active'); // 'active', 'archived', 'all'
  const [formData, setFormData] = useState({
    name: '', subtitle: '', description: '', description_html: '', badge: '', category_label: '', price: 0, billing_cycle: 'one_time',
    subscription_type: 'digital', character_count: 1,
    features: [], character_ids: [], is_published: true, is_giftable: true, is_featured: false, display_order: 0,
    image_url: '', image_alt: '', image_caption: '', card_layout: 'default',
    cta_text: '', cta_link: '', delivery_interval_days: 30,
    stripe_price_id: '', stripe_product_id: '', category: '', region: '', is_prepaid: false
  });
  const [newFeature, setNewFeature] = useState('');
  const [editingFeatureIndex, setEditingFeatureIndex] = useState(null);
  const [editingFeatureValue, setEditingFeatureValue] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const [tiersRes, charsRes] = await Promise.all([
        axios.get(`${API}/admin/memberships`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/characters`, { headers: getAuthHeaders() })
      ]);
      setTiers(tiersRes.data);
      setCharacters(charsRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/memberships/${editingId}`, formData, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/admin/memberships`, formData, { headers: getAuthHeaders() });
      }
      fetchData();
      resetForm();
    } catch (error) {
      alert('Failed to save membership');
    }
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setFormData({
      name: t.name, subtitle: t.subtitle || '', description: t.description || '', description_html: t.description_html || '',
      badge: t.badge || '', category_label: t.category_label || '', price: t.price, billing_cycle: t.billing_cycle,
      subscription_type: t.subscription_type || 'digital', character_count: t.character_count ?? 1,
      features: t.features || [], character_ids: t.character_ids || [],
      is_published: t.is_published, is_giftable: t.is_giftable, is_featured: t.is_featured || false, display_order: t.display_order || 0,
      image_url: t.image_url || '', image_alt: t.image_alt || '', image_caption: t.image_caption || '',
      card_layout: t.card_layout || 'default', cta_text: t.cta_text || '', cta_link: t.cta_link || '',
      delivery_interval_days: t.delivery_interval_days ?? 30,
      stripe_price_id: t.stripe_price_id || '', stripe_product_id: t.stripe_product_id || '',
      category: t.category || '', region: t.region || '', is_prepaid: t.is_prepaid || false
    });
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDelete = async (id) => {
    try {
      // First check subscriber count
      const countRes = await axios.get(`${API}/admin/memberships/${id}/subscriber-count`, { headers: getAuthHeaders() });
      const { active_subscribers } = countRes.data;
      
      if (active_subscribers > 0) {
        const proceed = window.confirm(
          `This tier has ${active_subscribers} active subscriber(s).\n\n` +
          `It will be ARCHIVED (hidden) but not deleted.\n` +
          `Existing subscriptions will remain active.\n\n` +
          `Continue?`
        );
        if (!proceed) return;
      } else {
        const proceed = window.confirm(
          `This tier has NO active subscribers.\n\n` +
          `It will be PERMANENTLY DELETED.\n\n` +
          `Continue?`
        );
        if (!proceed) return;
      }
      
      await axios.delete(`${API}/admin/memberships/${id}`, { headers: getAuthHeaders() });
      fetchData();
    } catch (error) {
      alert('Failed to delete membership');
    }
  };

  const handleArchive = async (id) => {
    try {
      await axios.put(`${API}/admin/memberships/${id}/archive`, {}, { headers: getAuthHeaders() });
      fetchData();
    } catch (error) {
      alert('Failed to archive membership');
    }
  };

  const handleRestore = async (id) => {
    try {
      await axios.put(`${API}/admin/memberships/${id}/restore`, {}, { headers: getAuthHeaders() });
      fetchData();
    } catch (error) {
      alert('Failed to restore membership');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: '', subtitle: '', description: '', description_html: '', badge: '', price: 0, billing_cycle: 'one_time',
      subscription_type: 'digital', character_count: 1,
      features: [], character_ids: [], is_published: true, is_giftable: true, is_featured: false, display_order: 0,
      image_url: '', image_alt: '', image_caption: '', card_layout: 'default',
      cta_text: '', cta_link: '', delivery_interval_days: 30,
      stripe_price_id: '', stripe_product_id: '', category: '', region: '', is_prepaid: false
    });
    setEditingFeatureIndex(null);
    setEditingFeatureValue('');
  };

  const addFeature = () => {
    if (newFeature.trim()) {
      setFormData({ ...formData, features: [...formData.features, newFeature.trim()] });
      setNewFeature('');
    }
  };

  const startEditFeature = (index) => {
    setEditingFeatureIndex(index);
    setEditingFeatureValue(formData.features[index]);
  };

  const saveEditFeature = () => {
    if (editingFeatureIndex !== null && editingFeatureValue.trim()) {
      const newFeatures = [...formData.features];
      newFeatures[editingFeatureIndex] = editingFeatureValue.trim();
      setFormData({ ...formData, features: newFeatures });
    }
    setEditingFeatureIndex(null);
    setEditingFeatureValue('');
  };

  const cancelEditFeature = () => {
    setEditingFeatureIndex(null);
    setEditingFeatureValue('');
  };

  const removeFeature = (index) => {
    setFormData({ ...formData, features: formData.features.filter((_, i) => i !== index) });
  };

  const moveFeature = (index, direction) => {
    const newFeatures = [...formData.features];
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= newFeatures.length) return;
    [newFeatures[index], newFeatures[newIndex]] = [newFeatures[newIndex], newFeatures[index]];
    setFormData({ ...formData, features: newFeatures });
  };

  const exportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/export/memberships`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'memberships.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to export CSV');
    }
  };

  if (loading) return <div className="text-white">Loading...</div>;

  // Filter tiers based on viewFilter
  const filteredTiers = tiers.filter(t => {
    if (viewFilter === 'active') return t.status === 'active' || !t.status;
    if (viewFilter === 'archived') return t.status === 'archived' || t.status === 'retired';
    return true; // 'all'
  });

  const activeTiersCount = tiers.filter(t => t.status === 'active' || !t.status).length;
  const archivedTiersCount = tiers.filter(t => t.status === 'archived' || t.status === 'retired').length;

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Membership Tiers</h2>
        <div className="flex gap-4 items-center">
          {/* View Filter Toggle */}
          <div className="flex bg-gray-700 rounded overflow-hidden">
            <button
              onClick={() => setViewFilter('active')}
              className={`px-3 py-1 text-sm ${viewFilter === 'active' ? 'bg-amber-600 text-white' : 'text-gray-300'}`}
            >
              Active ({activeTiersCount})
            </button>
            <button
              onClick={() => setViewFilter('archived')}
              className={`px-3 py-1 text-sm ${viewFilter === 'archived' ? 'bg-amber-600 text-white' : 'text-gray-300'}`}
            >
              Archived ({archivedTiersCount})
            </button>
            <button
              onClick={() => setViewFilter('all')}
              className={`px-3 py-1 text-sm ${viewFilter === 'all' ? 'bg-amber-600 text-white' : 'text-gray-300'}`}
            >
              All
            </button>
          </div>
          <button onClick={exportCSV} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm">
            Export CSV
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold text-white">{editingId ? 'Edit Membership' : 'Add Membership'}</h3>

        <div className="grid grid-cols-4 gap-4">
          <div className="col-span-2">
            <label className="block text-gray-300 text-sm mb-2">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
              required
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Price ($)</label>
            <input
              type="number"
              step="0.01"
              value={formData.price}
              onChange={(e) => setFormData({ ...formData, price: parseFloat(e.target.value) || 0 })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Billing Cycle</label>
            <select
              value={formData.billing_cycle}
              onChange={(e) => setFormData({ ...formData, billing_cycle: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            >
              <option value="one_time">One-Time</option>
              <option value="monthly">Monthly</option>
              <option value="quarterly">Quarterly</option>
              <option value="yearly">Yearly</option>
            </select>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 text-sm mb-2">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white h-24"
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Display Order</label>
            <input
              type="number"
              value={formData.display_order}
              onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            />
            <p className="text-gray-500 text-xs mt-1">Lower numbers appear first</p>
          </div>
        </div>

        {/* Membership Card Image */}
        <div>
          <FileUpload
            uploadType="memberships"
            onUpload={(url) => setFormData({ ...formData, image_url: url })}
            currentUrl={formData.image_url}
            label="Membership Card Image"
            accept="image/*"
          />
          <div className="grid grid-cols-2 gap-3 mt-2">
            <input type="text" value={formData.image_alt} onChange={(e) => setFormData({ ...formData, image_alt: e.target.value })} className="bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs" placeholder="Image alt text" />
            <input type="text" value={formData.image_caption} onChange={(e) => setFormData({ ...formData, image_caption: e.target.value })} className="bg-gray-700 border border-gray-600 rounded px-2 py-1.5 text-white text-xs" placeholder="Image caption (optional)" />
          </div>
        </div>

        {/* Subtitle, Badge, Card Layout */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-300 text-sm mb-2">Subtitle</label>
            <input type="text" value={formData.subtitle} onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white" placeholder="e.g. Monthly Digital" />
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Category Label</label>
            <input type="text" value={formData.category_label || ''} onChange={(e) => setFormData({ ...formData, category_label: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white" placeholder="e.g. PHYSICAL, DIGITAL, CHILDREN" />
            <p className="text-gray-500 text-xs mt-1">Overrides the default category name on the card</p>
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Badge</label>
            <input type="text" value={formData.badge} onChange={(e) => setFormData({ ...formData, badge: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white" placeholder="e.g. Most Popular" />
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Card Layout</label>
            <select value={formData.card_layout} onChange={(e) => setFormData({ ...formData, card_layout: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white">
              <option value="default">Grid Card</option>
              <option value="image_left">Image Left / Text Right</option>
              <option value="image_right">Image Right / Text Left</option>
              <option value="editorial">Editorial Feature</option>
            </select>
          </div>
        </div>

        {/* Rich Description */}
        <div>
          <label className="block text-gray-300 text-sm mb-2">Rich Description (HTML)</label>
          <textarea
            value={formData.description_html}
            onChange={(e) => setFormData({ ...formData, description_html: e.target.value })}
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white h-20 text-sm"
            placeholder="<p>Rich description with <strong>bold</strong> and <em>italic</em>...</p>"
          />
        </div>

        {/* Custom CTA */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 text-sm mb-2">Custom CTA Text</label>
            <input type="text" value={formData.cta_text} onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white" placeholder="Leave empty for default" />
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Custom CTA Link</label>
            <input type="text" value={formData.cta_link} onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white" placeholder="Leave empty for default flow" />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4">
          {/* Features Section with Inline Editing */}
          <div>
            <label className="block text-gray-300 text-sm mb-2">Features</label>
            <div className="flex gap-2 mb-2">
              <input
                type="text"
                value={newFeature}
                onChange={(e) => setNewFeature(e.target.value)}
                placeholder="Add feature..."
                className="flex-1 bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white text-sm"
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addFeature())}
              />
              <button type="button" onClick={addFeature} className="bg-gray-600 hover:bg-gray-500 text-white px-4 py-2 rounded text-sm">Add</button>
            </div>
            <div className="space-y-1 max-h-48 overflow-y-auto">
              {formData.features.map((f, i) => (
                <div key={i} className="flex items-center gap-2 bg-gray-700/50 rounded px-2 py-1">
                  {editingFeatureIndex === i ? (
                    <>
                      <input
                        type="text"
                        value={editingFeatureValue}
                        onChange={(e) => setEditingFeatureValue(e.target.value)}
                        className="flex-1 bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                        autoFocus
                        onKeyPress={(e) => e.key === 'Enter' && saveEditFeature()}
                      />
                      <button type="button" onClick={saveEditFeature} className="text-green-400 text-xs px-1">✓</button>
                      <button type="button" onClick={cancelEditFeature} className="text-gray-400 text-xs px-1">✕</button>
                    </>
                  ) : (
                    <>
                      <div className="flex gap-1">
                        <button type="button" onClick={() => moveFeature(i, 'up')} disabled={i === 0} className="text-gray-400 hover:text-white disabled:opacity-30 text-xs">↑</button>
                        <button type="button" onClick={() => moveFeature(i, 'down')} disabled={i === formData.features.length - 1} className="text-gray-400 hover:text-white disabled:opacity-30 text-xs">↓</button>
                      </div>
                      <span className="flex-1 text-gray-300 text-sm">• {f}</span>
                      <button type="button" onClick={() => startEditFeature(i)} className="text-amber-400 text-xs px-1">Edit</button>
                      <button type="button" onClick={() => removeFeature(i)} className="text-red-400 text-xs px-1">×</button>
                    </>
                  )}
                </div>
              ))}
              {formData.features.length === 0 && (
                <p className="text-gray-500 text-sm text-center py-2">No features added</p>
              )}
            </div>
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Included Characters</label>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {characters.map((c) => (
                <label key={c.id} className="flex items-center gap-2 text-gray-300 text-sm">
                  <input
                    type="checkbox"
                    checked={formData.character_ids.includes(c.id)}
                    onChange={(e) => {
                      const ids = e.target.checked
                        ? [...formData.character_ids, c.id]
                        : formData.character_ids.filter(id => id !== c.id);
                      setFormData({ ...formData, character_ids: ids });
                    }}
                    className="rounded bg-gray-700"
                  />
                  {c.name}
                </label>
              ))}
            </div>
          </div>
        </div>

        {/* Subscription Settings Row */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-gray-300 text-sm mb-2">Subscription Type</label>
            <select
              value={formData.subscription_type}
              onChange={(e) => setFormData({ ...formData, subscription_type: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            >
              <option value="digital">Digital Letters</option>
              <option value="physical">Physical Letters</option>
              <option value="mystery">Mystery / Surprise</option>
              <option value="kids">Kids Mail</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Character Count</label>
            <select
              value={formData.character_count}
              onChange={(e) => setFormData({ ...formData, character_count: parseInt(e.target.value) })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            >
              <option value={0}>0 — No character selection (mystery boxes, standalone products)</option>
              <option value={1}>1 Character</option>
              <option value={2}>2 Characters</option>
              <option value={3}>3 Characters</option>
            </select>
          </div>
        </div>

        {/* Delivery Interval */}
        <div>
          <label className="block text-gray-300 text-sm mb-2">Delivery Interval (days between letters)</label>
          <input
            type="number"
            value={formData.delivery_interval_days ?? 30}
            onChange={(e) => setFormData({ ...formData, delivery_interval_days: parseInt(e.target.value) || 30 })}
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            style={{maxWidth: '200px'}}
            min="1"
          />
          <p className="text-gray-500 text-xs mt-1">Default: 30 days. Overrides global interval for this tier.</p>
        </div>

        {/* Stripe Integration */}
        <div className="border-t border-gray-600 pt-4 mt-4">
          <h4 className="text-blue-400 text-sm font-medium mb-3 flex items-center gap-2">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" /></svg>
            Stripe Integration
          </h4>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-gray-300 text-sm mb-2">Stripe Price ID *</label>
              <input
                type="text"
                value={formData.stripe_price_id}
                onChange={(e) => setFormData({ ...formData, stripe_price_id: e.target.value.trim() })}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white font-mono text-sm"
                placeholder="price_1Abc..."
                data-testid="stripe-price-id-input"
              />
              <p className="text-gray-500 text-xs mt-1">From your Stripe Dashboard &gt; Products &gt; Pricing</p>
            </div>
            <div>
              <label className="block text-gray-300 text-sm mb-2">Stripe Product ID (optional)</label>
              <input
                type="text"
                value={formData.stripe_product_id}
                onChange={(e) => setFormData({ ...formData, stripe_product_id: e.target.value.trim() })}
                className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white font-mono text-sm"
                placeholder="prod_1Abc..."
              />
              <p className="text-gray-500 text-xs mt-1">Optional — for reference only</p>
            </div>
          </div>
          {formData.stripe_price_id && (
            <div className="mt-2 bg-blue-900/30 border border-blue-800/50 rounded px-3 py-2 text-blue-300 text-xs">
              Checkout will use Stripe Price ID: <span className="font-mono font-bold">{formData.stripe_price_id}</span>
              {formData.billing_cycle === 'monthly' || formData.billing_cycle === 'quarterly' || formData.billing_cycle === 'yearly'
                ? ' (creates recurring subscription)'
                : ' (creates one-time payment)'}
            </div>
          )}
        </div>

        {/* Category & Region */}
        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-300 text-sm mb-2">Category</label>
            <select
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            >
              <option value="">None</option>
              <option value="digital">Digital</option>
              <option value="physical">Physical</option>
              <option value="mystery">Mystery</option>
              <option value="prepaid">Prepaid</option>
              <option value="kids">Kids</option>
            </select>
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Region</label>
            <input type="text" value={formData.region} onChange={(e) => setFormData({ ...formData, region: e.target.value })} className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white" placeholder="e.g. australia, international" />
          </div>
          <div className="flex items-end pb-2">
            <label className="flex items-center gap-2 text-gray-300">
              <input type="checkbox" checked={formData.is_prepaid} onChange={(e) => setFormData({ ...formData, is_prepaid: e.target.checked })} className="rounded bg-gray-700" />
              Prepaid (one-time, not recurring)
            </label>
          </div>
        </div>

        <div className="flex gap-6">
          <label className="flex items-center gap-2 text-gray-300">
            <input type="checkbox" checked={formData.is_published} onChange={(e) => setFormData({ ...formData, is_published: e.target.checked })} className="rounded bg-gray-700" />
            Published
          </label>
          <label className="flex items-center gap-2 text-gray-300">
            <input type="checkbox" checked={formData.is_giftable} onChange={(e) => setFormData({ ...formData, is_giftable: e.target.checked })} className="rounded bg-gray-700" />
            Giftable
          </label>
          <label className="flex items-center gap-2 text-gray-300">
            <input type="checkbox" checked={formData.is_featured} onChange={(e) => setFormData({ ...formData, is_featured: e.target.checked })} className="rounded bg-gray-700" />
            <span className="text-amber-400">★ Most Beloved Badge</span>
          </label>
        </div>

        <div className="flex gap-4">
          <button type="submit" className="bg-amber-600 hover:bg-amber-500 text-white px-6 py-2 rounded">
            {editingId ? 'Update' : 'Create'}
          </button>
          {editingId && (
            <button type="button" onClick={resetForm} className="bg-gray-600 hover:bg-gray-500 text-white px-6 py-2 rounded">Cancel</button>
          )}
        </div>
      </form>

      {/* Tiers List */}
      <div className="space-y-4">
        {filteredTiers.length === 0 && (
          <div className="bg-gray-800 rounded-lg p-8 text-center">
            <p className="text-gray-400">
              {viewFilter === 'archived' ? 'No archived membership tiers' : 'No membership tiers found'}
            </p>
          </div>
        )}
        {filteredTiers.map((t) => (
          <div key={t.id} className={`bg-gray-800 rounded-lg p-4 ${(t.status === 'archived' || t.status === 'retired') ? 'opacity-60 border border-gray-700' : ''}`}>
            <div className="flex justify-between items-start">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  {t.image_url && <img src={t.image_url} alt="" className="w-10 h-10 rounded object-cover" />}
                  <h4 className="text-white font-medium text-lg">{t.name}</h4>
                  {t.is_featured && <span className="text-xs px-2 py-1 rounded bg-amber-900 text-amber-300">★ Most Beloved</span>}
                </div>
                <p className="text-amber-400 font-bold">${t.price?.toFixed(2)} / {t.billing_cycle?.replace('_', ' ')}</p>
                <p className="text-gray-400 text-sm mt-1">{t.description}</p>
                {t.features?.length > 0 && (
                  <div className="mt-2 text-gray-500 text-xs">
                    Features: {t.features.slice(0, 3).join(' • ')}{t.features.length > 3 ? ` +${t.features.length - 3} more` : ''}
                  </div>
                )}
                <div className="flex gap-2 mt-2 flex-wrap">
                  <span className={`text-xs px-2 py-1 rounded ${
                    t.status === 'active' || !t.status ? 'bg-green-900 text-green-300' : 
                    t.status === 'archived' || t.status === 'retired' ? 'bg-gray-700 text-gray-400' : 'bg-gray-700 text-gray-300'
                  }`}>
                    {t.status || 'active'}
                  </span>
                  <span className="text-xs px-2 py-1 rounded bg-purple-900 text-purple-300">
                    {t.subscription_type || 'digital'} • {t.character_count ?? 1} char
                  </span>
                  {t.stripe_price_id && <span className="text-xs px-2 py-1 rounded bg-blue-900 text-blue-300" title={t.stripe_price_id}>Stripe: {t.stripe_price_id.substring(0, 20)}...</span>}
                  {!t.stripe_price_id && <span className="text-xs px-2 py-1 rounded bg-red-900 text-red-300">No Stripe ID</span>}
                  {!t.is_published && <span className="text-xs px-2 py-1 rounded bg-yellow-900 text-yellow-300">Unpublished</span>}
                  <span className="text-xs text-gray-500">Order: {t.display_order || 0}</span>
                </div>
              </div>
              <div className="flex gap-2 flex-wrap justify-end">
                <button onClick={() => handleEdit(t)} className="text-amber-400 hover:text-amber-300 px-3 py-1 text-sm">Edit</button>
                {(t.status === 'archived' || t.status === 'retired') ? (
                  <>
                    <button onClick={() => handleRestore(t.id)} className="text-green-400 hover:text-green-300 px-3 py-1 text-sm">Restore</button>
                    <button onClick={() => handleDelete(t.id)} className="text-red-400 hover:text-red-300 px-3 py-1 text-sm">Delete</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => handleArchive(t.id)} className="text-yellow-400 hover:text-yellow-300 px-3 py-1 text-sm">Archive</button>
                    <button onClick={() => handleDelete(t.id)} className="text-red-400 hover:text-red-300 px-3 py-1 text-sm">Delete</button>
                  </>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============== SUBSCRIPTIONS TAB ==============
const SubscriptionsTab = ({ getAuthHeaders }) => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');
  const [viewMode, setViewMode] = useState('all'); // 'all' or 'shipments'
  const [shipmentsDue, setShipmentsDue] = useState([]);
  const [trackingInputs, setTrackingInputs] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [subsRes, custRes, tiersRes, shipmentsRes] = await Promise.all([
        axios.get(`${API}/admin/subscriptions`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/customers`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/memberships`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/subscriptions/shipments/due`, { headers: getAuthHeaders() })
      ]);
      setSubscriptions(subsRes.data);
      setCustomers(custRes.data);
      setTiers(tiersRes.data);
      setShipmentsDue(shipmentsRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getCustomerEmail = (id) => customers.find(c => c.id === id)?.email || 'Unknown';
  const getTierName = (sub) => {
    // First check if subscription has a special_subscription_name (Little Explorers Club, etc.)
    if (sub.special_subscription_name) {
      return sub.special_subscription_name;
    }
    // Otherwise look up the tier by ID
    const tier = tiers.find(t => t.id === sub.membership_tier_id);
    return tier?.name || 'Unknown';
  };
  
  // Format character slugs to display names if character_names not provided
  const getCharacterNames = (sub) => {
    if (sub.character_names && sub.character_names.length > 0) {
      return sub.character_names;
    }
    // Fall back to formatting slugs
    if (sub.selected_character_slugs && sub.selected_character_slugs.length > 0) {
      return sub.selected_character_slugs.map(slug => 
        slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')
      );
    }
    return [];
  };

  const handleAction = async (subId, action) => {
    try {
      await axios.put(`${API}/admin/subscriptions/${subId}/${action}`, {}, { headers: getAuthHeaders() });
      fetchData();
    } catch (error) {
      alert(`Failed to ${action} subscription`);
    }
  };

  const handleShip = async (subId) => {
    const tracking = trackingInputs[subId];
    if (!tracking || !tracking.trim()) {
      alert('Please enter a tracking number');
      return;
    }
    try {
      await axios.put(`${API}/admin/subscriptions/${subId}/ship?tracking_number=${encodeURIComponent(tracking)}`, {}, { headers: getAuthHeaders() });
      setTrackingInputs(prev => ({ ...prev, [subId]: '' }));
      fetchData();
      alert('Shipment marked as shipped and notification sent!');
    } catch (error) {
      alert('Failed to mark as shipped');
    }
  };

  const handleStatusChange = async (subId, status) => {
    try {
      await axios.put(`${API}/admin/subscriptions/${subId}/shipment-status?status=${status}`, {}, { headers: getAuthHeaders() });
      fetchData();
    } catch (error) {
      alert('Failed to update status');
    }
  };

  const filteredSubs = filter ? subscriptions.filter(s => s.status === filter) : subscriptions;

  const exportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/export/subscribers`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'subscribers.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to export CSV');
    }
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Subscriptions</h2>
        <div className="flex gap-4 items-center">
          {/* View Mode Toggle */}
          <div className="flex bg-gray-700 rounded overflow-hidden">
            <button
              onClick={() => setViewMode('all')}
              className={`px-4 py-1 text-sm ${viewMode === 'all' ? 'bg-amber-600 text-white' : 'text-gray-300'}`}
            >
              All Subscriptions
            </button>
            <button
              onClick={() => setViewMode('shipments')}
              className={`px-4 py-1 text-sm relative ${viewMode === 'shipments' ? 'bg-amber-600 text-white' : 'text-gray-300'}`}
            >
              Shipments Due
              {shipmentsDue.length > 0 && (
                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                  {shipmentsDue.length}
                </span>
              )}
            </button>
          </div>
          <button onClick={exportCSV} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-1 rounded text-sm">
            Export CSV
          </button>
        </div>
      </div>

      {viewMode === 'all' ? (
        <>
          {/* Filter Buttons */}
          <div className="flex gap-2">
            {['', 'active', 'paused', 'cancelled'].map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`px-3 py-1 rounded text-sm ${filter === f ? 'bg-amber-600 text-white' : 'bg-gray-700 text-gray-300'}`}
              >
                {f || 'All'}
              </button>
            ))}
          </div>

          <div className="space-y-4">
            {filteredSubs.map((sub) => {
              const charNames = getCharacterNames(sub);
              return (
              <div key={sub.id} className="bg-gray-800 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-white font-medium">{getCustomerEmail(sub.customer_id)}</p>
                    <p className="text-gray-400 text-sm">{getTierName(sub)}</p>
                    {/* Show character names */}
                    {charNames.length > 0 && (
                      <p className="text-amber-400 text-xs mt-1">Characters: {charNames.join(', ')}</p>
                    )}
                    <div className="flex gap-2 mt-2 flex-wrap">
                      <span className={`text-xs px-2 py-1 rounded ${
                        sub.status === 'active' ? 'bg-green-900 text-green-300' :
                        sub.status === 'paused' ? 'bg-yellow-900 text-yellow-300' :
                        'bg-red-900 text-red-300'
                      }`}>
                        {sub.status}
                      </span>
                      <span className={`text-xs px-2 py-1 rounded ${
                        sub.subscription_type === 'physical' ? 'bg-blue-900 text-blue-300' : 'bg-purple-900 text-purple-300'
                      }`}>
                        {sub.subscription_type}
                      </span>
                      {sub.is_gift && <span className="text-xs px-2 py-1 rounded bg-pink-900 text-pink-300">Gift</span>}
                      <span className="text-xs text-gray-400">
                        Letter #{sub.current_letter_index || 0}
                      </span>
                    </div>
                    {/* Show shipment status for physical subscriptions */}
                    {sub.subscription_type === 'physical' && (
                      <div className="mt-2 text-xs">
                        <span className={`px-2 py-1 rounded ${
                          sub.shipment_status === 'shipped' ? 'bg-green-900 text-green-300' :
                          sub.shipment_status === 'due' ? 'bg-red-900 text-red-300' :
                          sub.shipment_status === 'preparing' ? 'bg-yellow-900 text-yellow-300' :
                          'bg-gray-700 text-gray-400'
                        }`}>
                          Shipment: {sub.shipment_status || 'none'}
                        </span>
                        {sub.tracking_number && (
                          <span className="ml-2 text-gray-400">Tracking: {sub.tracking_number}</span>
                        )}
                      </div>
                    )}
                    {/* Show shipping address for physical subscriptions */}
                    {sub.subscription_type === 'physical' && sub.shipping_address && (
                      <div className="mt-2 p-2 bg-gray-700/50 rounded text-xs" data-testid={`sub-shipping-address-${sub.id}`}>
                        <p className="text-amber-400 font-semibold mb-1">Shipping Address:</p>
                        <div className="text-gray-300 space-y-0.5">
                          {sub.shipping_address.name && <p>{sub.shipping_address.name}</p>}
                          {sub.shipping_address.line1 && <p>{sub.shipping_address.line1}</p>}
                          {sub.shipping_address.line2 && <p>{sub.shipping_address.line2}</p>}
                          <p>
                            {[sub.shipping_address.city, sub.shipping_address.state, sub.shipping_address.postal_code].filter(Boolean).join(', ')}
                          </p>
                          {sub.shipping_address.country && <p>{sub.shipping_address.country}</p>}
                        </div>
                      </div>
                    )}
                    {/* Show next send date for digital subscriptions */}
                    {sub.subscription_type === 'digital' && sub.next_send_date && (
                      <p className="text-xs text-gray-500 mt-1">
                        Next letter: {new Date(sub.next_send_date).toLocaleDateString()}
                      </p>
                    )}
                  </div>
                  <div className="flex gap-2">
                    {sub.status === 'active' && (
                      <>
                        <button onClick={() => handleAction(sub.id, 'pause')} className="text-yellow-400 hover:text-yellow-300 text-sm">Pause</button>
                        <button onClick={() => handleAction(sub.id, 'cancel')} className="text-red-400 hover:text-red-300 text-sm">Cancel</button>
                      </>
                    )}
                    {sub.status === 'paused' && (
                      <button onClick={() => handleAction(sub.id, 'resume')} className="text-green-400 hover:text-green-300 text-sm">Resume</button>
                    )}
                  </div>
                </div>
              </div>
              );
            })}
          </div>
        </>
      ) : (
        /* Shipments Due View */
        <div className="space-y-4">
          {shipmentsDue.length === 0 ? (
            <div className="bg-gray-800 rounded-lg p-8 text-center">
              <p className="text-gray-400">No shipments currently due</p>
              <p className="text-gray-500 text-sm mt-2">Physical subscriptions will appear here when they're ready to ship</p>
            </div>
          ) : (
            shipmentsDue.map((sub) => (
              <div key={sub.id} className="bg-gray-800 rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3">
                      <p className="text-white font-medium">{sub.customer_email}</p>
                      <span className={`text-xs px-2 py-1 rounded ${
                        sub.shipment_status === 'due' ? 'bg-red-900 text-red-300' : 'bg-yellow-900 text-yellow-300'
                      }`}>
                        {sub.shipment_status === 'due' ? 'Due for Shipment' : 'Preparing'}
                      </span>
                    </div>
                    <p className="text-gray-400 text-sm">{sub.membership_name}</p>
                    {sub.character_names?.length > 0 && (
                      <p className="text-gray-500 text-xs mt-1">Characters: {sub.character_names.join(', ')}</p>
                    )}
                    <p className="text-gray-500 text-xs">Letter #{(sub.current_letter_index || 0) + 1}</p>
                    
                    {/* Shipping Address for Shipments Due */}
                    {sub.shipping_address && (
                      <div className="mt-3 p-3 bg-gray-700/50 rounded" data-testid={`shipment-address-${sub.id}`}>
                        <p className="text-amber-400 text-xs font-semibold mb-2 uppercase tracking-wide">Ship To:</p>
                        <div className="text-gray-200 text-sm space-y-0.5">
                          {sub.shipping_address.name && <p className="font-medium">{sub.shipping_address.name}</p>}
                          {sub.shipping_address.line1 && <p>{sub.shipping_address.line1}</p>}
                          {sub.shipping_address.line2 && <p>{sub.shipping_address.line2}</p>}
                          <p>
                            {[sub.shipping_address.city, sub.shipping_address.state, sub.shipping_address.postal_code].filter(Boolean).join(', ')}
                          </p>
                          {sub.shipping_address.country && <p>{sub.shipping_address.country}</p>}
                        </div>
                        {sub.shipping_address.email && (
                          <p className="text-gray-400 text-xs mt-2">Email: {sub.shipping_address.email}</p>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2 items-end">
                    {/* Status Toggle */}
                    <select
                      value={sub.shipment_status}
                      onChange={(e) => handleStatusChange(sub.id, e.target.value)}
                      className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm"
                    >
                      <option value="due">Due</option>
                      <option value="preparing">Preparing</option>
                    </select>
                    {/* Tracking & Ship */}
                    <div className="flex gap-2">
                      <input
                        type="text"
                        placeholder="Tracking #"
                        value={trackingInputs[sub.id] || ''}
                        onChange={(e) => setTrackingInputs(prev => ({ ...prev, [sub.id]: e.target.value }))}
                        className="bg-gray-700 border border-gray-600 rounded px-2 py-1 text-white text-sm w-40"
                      />
                      <button
                        onClick={() => handleShip(sub.id)}
                        className="bg-green-600 hover:bg-green-500 text-white px-3 py-1 rounded text-sm"
                      >
                        Mark Shipped
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}
    </div>
  );
};

// ============== ORDERS TAB ==============
const OrdersTab = ({ getAuthHeaders }) => {
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedOrder, setExpandedOrder] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [ordersRes, custRes] = await Promise.all([
        axios.get(`${API}/admin/orders`, { headers: getAuthHeaders() }),
        axios.get(`${API}/admin/customers`, { headers: getAuthHeaders() })
      ]);
      setOrders(ordersRes.data);
      setCustomers(custRes.data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getCustomerEmail = (id) => customers.find(c => c.id === id)?.email || 'Unknown';

  // Format shipping address for display
  const formatAddress = (address) => {
    if (!address) return null;
    const parts = [
      address.name,
      address.line1,
      address.line2,
      [address.city, address.state, address.postal_code].filter(Boolean).join(', '),
      address.country
    ].filter(Boolean);
    return parts;
  };

  const exportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/export/orders`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'orders.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to export CSV');
    }
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Orders</h2>
        <button onClick={exportCSV} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm">
          Export CSV
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="text-gray-400 text-sm border-b border-gray-700">
            <tr>
              <th className="pb-3">Order #</th>
              <th className="pb-3">Customer</th>
              <th className="pb-3">Total</th>
              <th className="pb-3">Status</th>
              <th className="pb-3">Payment</th>
              <th className="pb-3">Address</th>
              <th className="pb-3">Date</th>
            </tr>
          </thead>
          <tbody>
            {orders.map((order) => {
              const addressLines = formatAddress(order.shipping_address);
              const isExpanded = expandedOrder === order.id;
              
              return (
                <React.Fragment key={order.id}>
                  <tr className="border-b border-gray-800 text-sm">
                    <td className="py-3 text-white">{order.order_number}</td>
                    <td className="py-3">
                      <div className="text-gray-300">{getCustomerEmail(order.customer_id)}</div>
                      {order.shipping_address?.name && (
                        <div className="text-gray-500 text-xs">{order.shipping_address.name}</div>
                      )}
                    </td>
                    <td className="py-3 text-amber-400">${order.total?.toFixed(2)}</td>
                    <td className="py-3">
                      <span className={`text-xs px-2 py-1 rounded ${
                        order.status === 'paid' || order.status === 'completed' ? 'bg-green-900 text-green-300' :
                        order.status === 'pending' ? 'bg-yellow-900 text-yellow-300' :
                        'bg-gray-700 text-gray-300'
                      }`}>
                        {order.status}
                      </span>
                    </td>
                    <td className="py-3">
                      <span className={`text-xs px-2 py-1 rounded ${
                        order.payment_status === 'paid' ? 'bg-green-900 text-green-300' : 'bg-gray-700 text-gray-300'
                      }`}>
                        {order.payment_status}
                      </span>
                    </td>
                    <td className="py-3">
                      {addressLines ? (
                        <button 
                          onClick={() => setExpandedOrder(isExpanded ? null : order.id)}
                          className="text-xs px-2 py-1 rounded bg-blue-900 text-blue-300 hover:bg-blue-800 transition-colors"
                          data-testid={`view-address-btn-${order.id}`}
                        >
                          {isExpanded ? 'Hide' : 'View'}
                        </button>
                      ) : (
                        <span className="text-gray-500 text-xs">—</span>
                      )}
                    </td>
                    <td className="py-3 text-gray-400">{new Date(order.created_at).toLocaleDateString()}</td>
                  </tr>
                  {/* Expanded address row */}
                  {isExpanded && addressLines && (
                    <tr className="bg-gray-800/50">
                      <td colSpan="7" className="py-4 px-4">
                        <div className="flex gap-8">
                          <div>
                            <h4 className="text-amber-400 text-xs font-semibold mb-2 uppercase tracking-wide">Shipping Address</h4>
                            <div className="text-gray-300 text-sm space-y-0.5" data-testid={`shipping-address-${order.id}`}>
                              {addressLines.map((line, idx) => (
                                <div key={idx}>{line}</div>
                              ))}
                            </div>
                            {order.shipping_address?.email && (
                              <div className="text-gray-400 text-xs mt-2">
                                Email: {order.shipping_address.email}
                              </div>
                            )}
                          </div>
                          {order.items?.length > 0 && (
                            <div>
                              <h4 className="text-amber-400 text-xs font-semibold mb-2 uppercase tracking-wide">Items</h4>
                              <div className="text-gray-300 text-sm space-y-0.5">
                                {order.items.map((item, idx) => (
                                  <div key={idx}>
                                    {item.name} - ${item.price?.toFixed(2)} <span className="text-gray-500 text-xs">({item.type})</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============== CUSTOMERS TAB ==============
const CustomersTab = ({ getAuthHeaders }) => {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/customers`, { headers: getAuthHeaders() });
      setCustomers(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const exportCSV = async () => {
    try {
      const response = await axios.get(`${API}/admin/export/customers`, {
        headers: getAuthHeaders(),
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customers.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert('Failed to export CSV');
    }
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Customers</h2>
        <button onClick={exportCSV} className="bg-gray-700 hover:bg-gray-600 text-white px-4 py-2 rounded text-sm">
          Export CSV
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-left">
          <thead className="text-gray-400 text-sm border-b border-gray-700">
            <tr>
              <th className="pb-3">Email</th>
              <th className="pb-3">Name</th>
              <th className="pb-3">Orders</th>
              <th className="pb-3">Total Spent</th>
              <th className="pb-3">Joined</th>
            </tr>
          </thead>
          <tbody>
            {customers.map((c) => (
              <tr key={c.id} className="border-b border-gray-800 text-sm">
                <td className="py-3 text-white">{c.email}</td>
                <td className="py-3 text-gray-300">{c.first_name} {c.last_name}</td>
                <td className="py-3 text-gray-300">{c.order_count || 0}</td>
                <td className="py-3 text-amber-400">${c.total_spent?.toFixed(2) || '0.00'}</td>
                <td className="py-3 text-gray-400">{new Date(c.created_at).toLocaleDateString()}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// ============== EMAILS TAB ==============
const EmailsTab = ({ getAuthHeaders }) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '', slug: '', subject: '', body_html: '', body_text: '',
    trigger_type: 'manual', is_active: true
  });
  const [broadcastSubject, setBroadcastSubject] = useState('');
  const [broadcastContent, setBroadcastContent] = useState('');

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/email-templates`, { headers: getAuthHeaders() });
      setTemplates(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingId) {
        await axios.put(`${API}/admin/email-templates/${editingId}`, formData, { headers: getAuthHeaders() });
      } else {
        await axios.post(`${API}/admin/email-templates`, formData, { headers: getAuthHeaders() });
      }
      fetchData();
      resetForm();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Unknown error';
      alert(`Failed to save template: ${errorMsg}`);
      console.error('Template save error:', error.response?.data || error);
    }
  };

  const handleEdit = (t) => {
    setEditingId(t.id);
    setFormData({
      name: t.name, slug: t.slug, subject: t.subject, body_html: t.body_html || '',
      body_text: t.body_text || '', trigger_type: t.trigger_type, is_active: t.is_active
    });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    await axios.delete(`${API}/admin/email-templates/${id}`, { headers: getAuthHeaders() });
    fetchData();
  };

  const sendTestEmail = async (templateId) => {
    const email = prompt('Enter test email address:');
    if (!email) return;
    try {
      await axios.post(`${API}/admin/email-templates/${templateId}/send-test?to_email=${email}`, {}, { headers: getAuthHeaders() });
      alert('Test email sent!');
    } catch (error) {
      alert('Failed to send test email');
    }
  };

  const sendBroadcast = async () => {
    if (!broadcastSubject || !broadcastContent) {
      alert('Please fill in subject and content');
      return;
    }
    if (!window.confirm('Send broadcast to all customers?')) return;
    try {
      const res = await axios.post(`${API}/admin/email-templates/broadcast`, null, {
        params: { subject: broadcastSubject, html_content: broadcastContent },
        headers: getAuthHeaders()
      });
      alert(`Broadcast sent: ${res.data.sent} successful, ${res.data.failed} failed`);
      setBroadcastSubject('');
      setBroadcastContent('');
    } catch (error) {
      alert('Failed to send broadcast');
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setFormData({
      name: '', slug: '', subject: '', body_html: '', body_text: '',
      trigger_type: 'manual', is_active: true
    });
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-white">Email Automation</h2>

      {/* Broadcast Section */}
      <div className="bg-gray-800 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold text-white">Send Broadcast</h3>
        <input
          type="text"
          value={broadcastSubject}
          onChange={(e) => setBroadcastSubject(e.target.value)}
          placeholder="Subject"
          className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
        />
        <textarea
          value={broadcastContent}
          onChange={(e) => setBroadcastContent(e.target.value)}
          placeholder="HTML content..."
          className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white h-32"
        />
        <button onClick={sendBroadcast} className="bg-green-600 hover:bg-green-500 text-white px-6 py-2 rounded">
          Send to All Customers
        </button>
      </div>

      {/* Template Form */}
      <form onSubmit={handleSubmit} className="bg-gray-800 rounded-lg p-6 space-y-4">
        <h3 className="text-lg font-semibold text-white">{editingId ? 'Edit Template' : 'Add Email Template'}</h3>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <label className="block text-gray-300 text-sm mb-2">Name *</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
              required
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Slug *</label>
            <input
              type="text"
              value={formData.slug}
              onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
              required
            />
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-2">Trigger</label>
            <select
              value={formData.trigger_type}
              onChange={(e) => setFormData({ ...formData, trigger_type: e.target.value })}
              className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            >
              <option value="manual">Manual</option>
              <option value="purchase">On Purchase</option>
              <option value="subscription_start">On Subscription Start</option>
              <option value="letter_delivery">On Letter Delivery</option>
              <option value="welcome">Welcome Email</option>
              <option value="gift_received">Gift Received</option>
            </select>
          </div>
        </div>

        <div>
          <label className="block text-gray-300 text-sm mb-2">Subject</label>
          <input
            type="text"
            value={formData.subject}
            onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
            placeholder="Use {{customer_name}} for variables"
          />
        </div>

        <div>
          <label className="block text-gray-300 text-sm mb-2">Body (HTML)</label>
          <textarea
            value={formData.body_html}
            onChange={(e) => setFormData({ ...formData, body_html: e.target.value })}
            className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white h-40"
            placeholder="<p>Hello {{customer_name}}...</p>"
          />
        </div>

        <div className="flex gap-4">
          <button type="submit" className="bg-amber-600 hover:bg-amber-500 text-white px-6 py-2 rounded">
            {editingId ? 'Update' : 'Create'}
          </button>
          {editingId && (
            <button type="button" onClick={resetForm} className="bg-gray-600 hover:bg-gray-500 text-white px-6 py-2 rounded">Cancel</button>
          )}
        </div>
      </form>

      {/* Templates List */}
      <div className="space-y-4">
        {templates.map((t) => (
          <div key={t.id} className="bg-gray-800 rounded-lg p-4 flex justify-between items-center">
            <div>
              <h4 className="text-white font-medium">{t.name}</h4>
              <p className="text-gray-400 text-sm">Trigger: {t.trigger_type} | Subject: {t.subject}</p>
            </div>
            <div className="flex gap-2">
              <button onClick={() => sendTestEmail(t.id)} className="text-green-400 hover:text-green-300 text-sm">Test</button>
              <button onClick={() => handleEdit(t)} className="text-amber-400 hover:text-amber-300 text-sm">Edit</button>
              <button onClick={() => handleDelete(t.id)} className="text-red-400 hover:text-red-300 text-sm">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============== MEDIA TAB ==============
const MediaTab = ({ getAuthHeaders }) => {
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/media`, { headers: getAuthHeaders() });
      setMedia(res.data);
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  }, [getAuthHeaders]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    setUploading(true);
    for (const file of files) {
      const formData = new FormData();
      formData.append('file', file);
      try {
        await axios.post(`${API}/admin/media/upload`, formData, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' }
        });
      } catch (error) {
        console.error('Upload error:', error);
      }
    }
    fetchData();
    setUploading(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this file?')) return;
    await axios.delete(`${API}/admin/media/${id}`, { headers: getAuthHeaders() });
    fetchData();
  };

  const copyUrl = (url) => {
    // Always copy relative URL - works on any domain
    const relativeUrl = url.startsWith('http') ? new URL(url).pathname : url;
    navigator.clipboard.writeText(relativeUrl);
    alert('URL copied! (Relative URL - works on any domain)');
  };

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-8">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white">Media Library</h2>
        <label className="bg-amber-600 hover:bg-amber-500 text-white px-4 py-2 rounded cursor-pointer">
          {uploading ? 'Uploading...' : 'Upload Files'}
          <input type="file" multiple onChange={handleUpload} className="hidden" />
        </label>
      </div>

      <div className="grid grid-cols-4 gap-4">
        {media.map((m) => (
          <div key={m.id} className="bg-gray-800 rounded-lg p-3">
            {m.file_type === 'image' ? (
              <img
                src={m.file_url}
                alt={m.original_filename}
                className="w-full h-32 object-cover rounded mb-2"
              />
            ) : (
              <div className="w-full h-32 bg-gray-700 rounded mb-2 flex items-center justify-center text-3xl">
                {m.file_type === 'pdf' ? '📄' : '📁'}
              </div>
            )}
            <p className="text-white text-sm truncate">{m.original_filename}</p>
            <div className="flex gap-2 mt-2">
              <button onClick={() => copyUrl(m.file_url)} className="text-amber-400 text-xs">Copy URL</button>
              <button onClick={() => handleDelete(m.id)} className="text-red-400 text-xs">Delete</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============== CUSTOM PAGES TAB ==============
const CustomPagesTab = ({ getAuthHeaders }) => {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingPage, setEditingPage] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content_html: '',
    seo_title: '',
    meta_description: '',
    is_published: false,
    show_in_nav: false,
    show_in_footer: false,
    nav_label: '',
    footer_label: '',
    nav_order: 100,
    footer_order: 100
  });

  const API = process.env.REACT_APP_BACKEND_URL || '';

  useEffect(() => {
    fetchPages();
  }, []);

  const fetchPages = async () => {
    try {
      const response = await fetch(`${API}/api/admin/custom-pages`, {
        headers: getAuthHeaders()
      });
      if (response.ok) {
        const data = await response.json();
        setPages(data);
      }
    } catch (error) {
      console.error('Failed to fetch custom pages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    const url = editingPage 
      ? `${API}/api/admin/custom-pages/${editingPage.id}`
      : `${API}/api/admin/custom-pages`;
    
    const method = editingPage ? 'PUT' : 'POST';
    
    try {
      const response = await fetch(url, {
        method,
        headers: {
          ...getAuthHeaders(),
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      if (response.ok) {
        fetchPages();
        setShowModal(false);
        setEditingPage(null);
        setFormData({
          title: '',
          slug: '',
          content_html: '',
          seo_title: '',
          meta_description: '',
          is_published: false,
          show_in_nav: false,
          show_in_footer: false,
          nav_label: '',
          footer_label: '',
          nav_order: 100,
          footer_order: 100
        });
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save page');
      }
    } catch (error) {
      console.error('Failed to save page:', error);
      alert('Failed to save page');
    }
  };

  const handleEdit = (page) => {
    setEditingPage(page);
    setFormData({
      title: page.title || '',
      slug: page.slug || '',
      content_html: page.content_html || '',
      seo_title: page.seo_title || '',
      meta_description: page.meta_description || '',
      is_published: page.is_published || false,
      show_in_nav: page.show_in_nav || false,
      show_in_footer: page.show_in_footer || false,
      nav_label: page.nav_label || '',
      footer_label: page.footer_label || '',
      nav_order: page.nav_order || 100,
      footer_order: page.footer_order || 100
    });
    setShowModal(true);
  };

  const handleDelete = async (pageId) => {
    if (!window.confirm('Are you sure you want to delete this page?')) return;
    
    try {
      const response = await fetch(`${API}/api/admin/custom-pages/${pageId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      
      if (response.ok) {
        fetchPages();
      }
    } catch (error) {
      console.error('Failed to delete page:', error);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-2xl font-bold text-white">Custom Pages</h2>
          <p className="text-gray-400 text-sm mt-1">
            Create and manage additional site pages (FAQ, Disclosures, Lore, etc.)
          </p>
        </div>
        <button
          onClick={() => {
            setEditingPage(null);
            setFormData({
              title: '',
              slug: '',
              content_html: '',
              seo_title: '',
              meta_description: '',
              is_published: false,
              show_in_nav: false,
              show_in_footer: false,
              nav_label: '',
              footer_label: '',
              nav_order: 100,
              footer_order: 100
            });
            setShowModal(true);
          }}
          className="bg-gold-500 hover:bg-gold-600 text-black px-4 py-2 rounded font-medium"
        >
          + Create New Page
        </button>
      </div>

      {/* Pages List */}
      {pages.length === 0 ? (
        <div className="bg-gray-800 rounded-lg p-8 text-center">
          <p className="text-gray-400 mb-4">No custom pages created yet.</p>
          <button
            onClick={() => setShowModal(true)}
            className="text-gold-400 hover:text-gold-300"
          >
            Create your first page →
          </button>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-700">
              <tr>
                <th className="text-left p-4 text-gray-300 text-sm font-medium">Page Title</th>
                <th className="text-left p-4 text-gray-300 text-sm font-medium">URL Slug</th>
                <th className="text-left p-4 text-gray-300 text-sm font-medium">Status</th>
                <th className="text-left p-4 text-gray-300 text-sm font-medium">Placement</th>
                <th className="text-right p-4 text-gray-300 text-sm font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {pages.map((page) => (
                <tr key={page.id} className="border-t border-gray-700 hover:bg-gray-700/50">
                  <td className="p-4 text-white">{page.title}</td>
                  <td className="p-4">
                    <code className="text-gold-400 text-sm bg-gray-700 px-2 py-1 rounded">
                      /pages/{page.slug}
                    </code>
                  </td>
                  <td className="p-4">
                    <span className={`text-xs px-2 py-1 rounded ${page.is_published ? 'bg-green-500/20 text-green-400' : 'bg-gray-600 text-gray-400'}`}>
                      {page.is_published ? 'Published' : 'Draft'}
                    </span>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-2">
                      {page.show_in_nav && (
                        <span className="text-xs px-2 py-1 rounded bg-blue-500/20 text-blue-400">Nav</span>
                      )}
                      {page.show_in_footer && (
                        <span className="text-xs px-2 py-1 rounded bg-purple-500/20 text-purple-400">Footer</span>
                      )}
                      {!page.show_in_nav && !page.show_in_footer && (
                        <span className="text-xs text-gray-500">Hidden</span>
                      )}
                    </div>
                  </td>
                  <td className="p-4 text-right space-x-2">
                    <a
                      href={`/pages/${page.slug}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-400 hover:text-blue-300 text-sm"
                    >
                      View
                    </a>
                    <button
                      onClick={() => handleEdit(page)}
                      className="text-gold-400 hover:text-gold-300 text-sm"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(page.id)}
                      className="text-red-400 hover:text-red-300 text-sm"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-800 rounded-lg w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gray-800 p-6 border-b border-gray-700 flex justify-between items-center">
              <h3 className="text-xl font-bold text-white">
                {editingPage ? 'Edit Page' : 'Create New Page'}
              </h3>
              <button
                onClick={() => setShowModal(false)}
                className="text-gray-400 hover:text-white text-2xl"
              >
                ×
              </button>
            </div>
            
            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              {/* Title */}
              <div>
                <label className="block text-gray-300 text-sm mb-1">Page Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => {
                    const title = e.target.value;
                    setFormData(prev => ({
                      ...prev,
                      title,
                      slug: prev.slug || generateSlug(title)
                    }));
                  }}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                  placeholder="e.g., Fiction & Creative Disclosure"
                  required
                />
              </div>

              {/* Slug */}
              <div>
                <label className="block text-gray-300 text-sm mb-1">URL Slug *</label>
                <div className="flex items-center gap-2">
                  <span className="text-gray-500">/pages/</span>
                  <input
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') }))}
                    className="flex-1 bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                    placeholder="fiction-disclosure"
                    required
                  />
                </div>
                <p className="text-gray-500 text-xs mt-1">
                  This will be the URL: /pages/{formData.slug || 'your-slug'}
                </p>
              </div>

              {/* Content */}
              <div>
                <label className="block text-gray-300 text-sm mb-1">Page Content (HTML)</label>
                <textarea
                  value={formData.content_html}
                  onChange={(e) => setFormData(prev => ({ ...prev, content_html: e.target.value }))}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white font-mono text-sm"
                  rows={12}
                  placeholder="<h2>Section Title</h2>&#10;<p>Your content here...</p>"
                />
                {formData.content_html && (
                  <div className="mt-3 p-4 bg-gray-900 rounded border border-gray-700">
                    <p className="text-gray-400 text-xs mb-2">Preview:</p>
                    <div 
                      className="prose prose-invert max-w-none text-sm"
                      dangerouslySetInnerHTML={{ __html: formData.content_html }}
                    />
                  </div>
                )}
              </div>

              {/* SEO Section */}
              <div className="border-t border-gray-700 pt-4 mt-4">
                <h4 className="text-white font-medium mb-3">SEO Settings (Optional)</h4>
                
                <div className="space-y-3">
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">SEO Title</label>
                    <input
                      type="text"
                      value={formData.seo_title}
                      onChange={(e) => setFormData(prev => ({ ...prev, seo_title: e.target.value }))}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                      placeholder="Custom title for search engines (defaults to page title)"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-gray-300 text-sm mb-1">Meta Description</label>
                    <textarea
                      value={formData.meta_description}
                      onChange={(e) => setFormData(prev => ({ ...prev, meta_description: e.target.value }))}
                      className="w-full bg-gray-700 border border-gray-600 rounded px-3 py-2 text-white"
                      rows={2}
                      placeholder="Brief description for search engine results"
                    />
                  </div>
                </div>
              </div>

              {/* Publish Toggle */}
              <div className="flex items-center justify-between bg-gray-700/50 rounded px-4 py-3">
                <div>
                  <span className="text-white font-medium">Publish Page</span>
                  <p className="text-gray-400 text-xs">Make this page visible to visitors</p>
                </div>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, is_published: !prev.is_published }))}
                  className={`relative w-12 h-6 rounded-full transition-colors ${formData.is_published ? 'bg-green-500' : 'bg-gray-600'}`}
                >
                  <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${formData.is_published ? 'left-7' : 'left-1'}`} />
                </button>
              </div>

              {/* Display Settings */}
              <div className="border-t border-gray-700 pt-4 mt-4">
                <h4 className="text-white font-medium mb-3">Display Settings</h4>
                <p className="text-gray-500 text-xs mb-4">Control where this page appears on your site</p>
                
                {/* Show in Navigation */}
                <div className="flex items-center justify-between bg-gray-700/30 rounded px-4 py-3 mb-3">
                  <div>
                    <span className="text-gray-300 text-sm">Show in Navigation</span>
                    <p className="text-gray-500 text-xs">Add this page to the main menu</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, show_in_nav: !prev.show_in_nav }))}
                    className={`relative w-12 h-6 rounded-full transition-colors ${formData.show_in_nav ? 'bg-gold-500' : 'bg-gray-600'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${formData.show_in_nav ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>

                {formData.show_in_nav && (
                  <div className="grid grid-cols-2 gap-3 mb-4 pl-4 border-l-2 border-gold-500/30">
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Navigation Label</label>
                      <input
                        type="text"
                        value={formData.nav_label}
                        onChange={(e) => setFormData(prev => ({ ...prev, nav_label: e.target.value }))}
                        className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                        placeholder={formData.title || "Uses page title"}
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Display Order</label>
                      <input
                        type="number"
                        value={formData.nav_order}
                        onChange={(e) => setFormData(prev => ({ ...prev, nav_order: parseInt(e.target.value) || 100 }))}
                        className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                        min="0"
                      />
                    </div>
                  </div>
                )}

                {/* Show in Footer */}
                <div className="flex items-center justify-between bg-gray-700/30 rounded px-4 py-3 mb-3">
                  <div>
                    <span className="text-gray-300 text-sm">Show in Footer</span>
                    <p className="text-gray-500 text-xs">Add this page to the footer links</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, show_in_footer: !prev.show_in_footer }))}
                    className={`relative w-12 h-6 rounded-full transition-colors ${formData.show_in_footer ? 'bg-gold-500' : 'bg-gray-600'}`}
                  >
                    <span className={`absolute top-1 w-4 h-4 bg-white rounded-full transition-transform ${formData.show_in_footer ? 'left-7' : 'left-1'}`} />
                  </button>
                </div>

                {formData.show_in_footer && (
                  <div className="grid grid-cols-2 gap-3 pl-4 border-l-2 border-gold-500/30">
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Footer Label</label>
                      <input
                        type="text"
                        value={formData.footer_label}
                        onChange={(e) => setFormData(prev => ({ ...prev, footer_label: e.target.value }))}
                        className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                        placeholder={formData.title || "Uses page title"}
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-xs mb-1">Display Order</label>
                      <input
                        type="number"
                        value={formData.footer_order}
                        onChange={(e) => setFormData(prev => ({ ...prev, footer_order: parseInt(e.target.value) || 100 }))}
                        className="w-full bg-gray-600 border border-gray-500 rounded px-2 py-1 text-white text-sm"
                        min="0"
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="flex justify-end gap-3 pt-4 border-t border-gray-700">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 text-gray-400 hover:text-white"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="bg-gold-500 hover:bg-gold-600 text-black px-6 py-2 rounded font-medium"
                >
                  {editingPage ? 'Save Changes' : 'Create Page'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

// ============== PAGES TAB ==============
const PagesTab = ({ getAuthHeaders }) => {
  const [pages, setPages] = useState({});
  const [loading, setLoading] = useState(true);
  const [activePageKey, setActivePageKey] = useState('home_hero');
  const [formData, setFormData] = useState({});

  const pageDefinitions = [
    // Homepage Sections (priority)
    { key: 'home_hero', label: '🏠 Homepage: Hero', fields: [
      { name: 'welcome_text', label: 'Welcome Text (small text above title)', type: 'text' },
      { name: 'title', label: 'Main Title', type: 'text' },
      { name: 'subtitle', label: 'Subtitle (below title)', type: 'text' },
      { name: 'tagline', label: 'Tagline (description text)', type: 'textarea' },
      { name: 'hero_image', label: 'Hero Background Image URL', type: 'image' },
      { name: 'diamond_symbol', label: 'Diamond Symbol Image URL', type: 'image' }
    ]},
    { key: 'home_prophecy', label: '🏠 Homepage: Prophecy Quote', fields: [
      { name: 'quote', label: 'Prophecy Quote', type: 'textarea' },
      { name: 'attribution', label: 'Attribution (e.g., — The First Binding)', type: 'text' }
    ]},
    { key: 'home_coming_soon', label: '🏠 Homepage: Coming Soon', fields: [
      { name: 'title', label: 'Title (first line)', type: 'text' },
      { name: 'subtitle', label: 'Subtitle (second line)', type: 'text' },
      { name: 'tagline', label: 'Tagline (third line)', type: 'text' }
    ]},
    { key: 'home_cta', label: '🏠 Homepage: Call to Action', fields: [
      { name: 'title', label: 'Heading', type: 'text' },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'primary_button', label: 'Primary Button Text', type: 'text' },
      { name: 'secondary_button', label: 'Secondary Button Text', type: 'text' }
    ]},
    { key: 'site_settings', label: '⚙️ Site Settings', fields: [
      { name: 'site_name', label: 'Site Name', type: 'text' },
      { name: 'contact_email', label: 'Contact Email', type: 'text' },
      { name: 'location', label: 'Business Location', type: 'text' },
      { name: 'footer_text', label: 'Footer Text', type: 'text' }
    ]},
    // Policy Pages
    { key: 'policy_terms', label: '📜 Terms & Conditions', fields: [
      { name: 'title', label: 'Page Title', type: 'text' },
      { name: 'body_html', label: 'Content (HTML supported)', type: 'richtext' },
      { name: 'last_updated', label: 'Last Updated', type: 'text' }
    ]},
    { key: 'policy_privacy', label: '🔒 Privacy Policy', fields: [
      { name: 'title', label: 'Page Title', type: 'text' },
      { name: 'body_html', label: 'Content (HTML supported)', type: 'richtext' },
      { name: 'last_updated', label: 'Last Updated', type: 'text' }
    ]},
    { key: 'policy_refund', label: '💳 Refund Policy', fields: [
      { name: 'title', label: 'Page Title', type: 'text' },
      { name: 'body_html', label: 'Content (HTML supported)', type: 'richtext' },
      { name: 'last_updated', label: 'Last Updated', type: 'text' }
    ]},
    { key: 'policy_shipping', label: '📦 Shipping Policy', fields: [
      { name: 'title', label: 'Page Title', type: 'text' },
      { name: 'body_html', label: 'Content (HTML supported)', type: 'richtext' },
      { name: 'last_updated', label: 'Last Updated', type: 'text' }
    ]},
    { key: 'contact', label: '📧 Contact Page', fields: [
      { name: 'title', label: 'Page Title', type: 'text' },
      { name: 'intro', label: 'Introduction Text', type: 'textarea' },
      { name: 'response_time', label: 'Response Time Note', type: 'text' }
    ]},
    // Other Pages
    { key: 'about', label: 'About Page', fields: [
      { name: 'page_label', label: 'Page Label (small text above title)', type: 'text' },
      { name: 'title', label: 'Page Title', type: 'text' },
      { name: 'intro', label: 'Introduction', type: 'textarea' },
      { name: 'philosophy', label: 'Philosophy', type: 'textarea' },
      { name: 'mission', label: 'Mission', type: 'textarea' },
      { name: 'quote', label: 'Featured Quote', type: 'textarea' },
      { name: 'closing', label: 'Closing', type: 'textarea' }
    ]},
    { key: 'about_author', label: 'About the Author', fields: [
      { name: 'page_label', label: 'Page Label (small text above title)', type: 'text' },
      { name: 'title', label: 'Page Title', type: 'text' },
      { name: 'author_photo', label: 'Author Photo', type: 'image_upload', uploadType: 'media' },
      { name: 'intro', label: 'Introduction', type: 'textarea' },
      { name: 'bio', label: 'Author Bio', type: 'textarea' },
      { name: 'body_html', label: 'Page Content (HTML supported)', type: 'richtext' }
    ]},
    { key: 'faq', label: 'FAQ Page', fields: [
      { name: 'page_label', label: 'Page Label (small text above title)', type: 'text' },
      { name: 'title', label: 'Page Title', type: 'text' },
      { name: 'intro', label: 'Introduction', type: 'textarea' },
      { name: 'body_html', label: 'FAQ Content (HTML supported)', type: 'richtext' }
    ]},
    { key: 'reviews', label: 'Reader Reflections', fields: [
      { name: 'page_label', label: 'Page Label (small text above title)', type: 'text' },
      { name: 'title', label: 'Page Title', type: 'text' },
      { name: 'hero_image', label: 'Hero Image', type: 'image_upload', uploadType: 'media' },
      { name: 'intro', label: 'Introduction', type: 'textarea' },
      { name: 'body_html', label: 'Reviews Content (HTML supported)', type: 'richtext' }
    ]},
    { key: 'membership', label: 'Membership Page', fields: [
      { name: 'page_label', label: 'Page Label', type: 'text' },
      { name: 'title', label: 'Page Title', type: 'text' },
      { name: 'description', label: 'Description', type: 'textarea' },
      { name: 'headline', label: 'Headline', type: 'text' },
      { name: 'subheadline', label: 'Subheadline', type: 'textarea' },
      { name: 'recommendation', label: 'Recommendation Text', type: 'text' },
      { name: 'card_layout', label: 'Default Card Layout (grid or editorial)', type: 'text' },
      { name: 'digital_title', label: 'Digital Letters — Section Title', type: 'text' },
      { name: 'digital_description', label: 'Digital Letters — Section Description', type: 'text' },
      { name: 'physical_title', label: 'Physical Letters — Section Title', type: 'text' },
      { name: 'physical_description', label: 'Physical Letters — Section Description', type: 'text' },
      { name: 'mystery_title', label: 'Mystery Letters — Section Title', type: 'text' },
      { name: 'mystery_description', label: 'Mystery Letters — Section Description', type: 'text' },
      { name: 'prepaid_title', label: 'Prepaid Digital — Section Title', type: 'text' },
      { name: 'prepaid_description', label: 'Prepaid Digital — Section Description', type: 'text' },
      { name: 'gift_label', label: 'Gift Section Label', type: 'text' },
      { name: 'gift_title', label: 'Gift Section Title', type: 'text' },
      { name: 'gift_description', label: 'Gift Section Description', type: 'textarea' },
      { name: 'gift_button', label: 'Gift Button Text', type: 'text' }
    ]}
  ];

  const fetchData = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/pages`, { headers: getAuthHeaders() });
      const pagesMap = {};
      res.data.forEach(p => { pagesMap[p.page_key] = p.content; });
      setPages(pagesMap);
      setFormData(pagesMap[activePageKey] || {});
    } catch (error) {
      console.error('Error:', error);
    }
    setLoading(false);
  }, [getAuthHeaders, activePageKey]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    setFormData(pages[activePageKey] || {});
  }, [activePageKey, pages]);

  const handleSave = async () => {
    try {
      // Send formData directly as the content, not wrapped in { content: ... }
      await axios.put(`${API}/admin/pages/${activePageKey}`, formData, { headers: getAuthHeaders() });
      setPages({ ...pages, [activePageKey]: formData });
      alert('Saved!');
    } catch (error) {
      alert('Failed to save');
    }
  };

  const activePageDef = pageDefinitions.find(p => p.key === activePageKey);

  if (loading) return <div className="text-white">Loading...</div>;

  return (
    <div className="space-y-8">
      <h2 className="text-2xl font-bold text-white">Page Content</h2>

      <div className="flex gap-2">
        {pageDefinitions.map((p) => (
          <button
            key={p.key}
            onClick={() => setActivePageKey(p.key)}
            className={`px-4 py-2 rounded ${activePageKey === p.key ? 'bg-amber-600 text-white' : 'bg-gray-700 text-gray-300'}`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {activePageDef && (
        <div className="bg-gray-800 rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold text-white">{activePageDef.label}</h3>
          {activePageDef.fields.map((field) => (
            <div key={field.name}>
              <label className="block text-gray-300 text-sm mb-2">{field.label}</label>
              {field.type === 'richtext' ? (
                <div>
                  <textarea
                    value={formData[field.name] || ''}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white h-48 font-mono text-sm"
                    placeholder="<p>Your HTML content here...</p>"
                  />
                  <p className="text-gray-500 text-xs mt-1">HTML tags supported: &lt;p&gt;, &lt;h2&gt;, &lt;h3&gt;, &lt;ul&gt;, &lt;li&gt;, &lt;strong&gt;, &lt;em&gt;, &lt;br&gt;</p>
                </div>
              ) : field.type === 'image_upload' ? (
                <div className="space-y-2">
                  <FileUpload
                    uploadType={field.uploadType || 'media'}
                    onUpload={(url) => setFormData({ ...formData, [field.name]: url })}
                    currentUrl={formData[field.name]}
                    label=""
                    accept="image/*"
                  />
                  {formData[field.name] && (
                    <div className="flex items-center gap-3 mt-2">
                      <img 
                        src={formData[field.name]} 
                        alt="Preview" 
                        className="max-w-xs max-h-32 rounded border border-gray-600"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                      <button
                        type="button"
                        onClick={() => setFormData({ ...formData, [field.name]: '' })}
                        className="text-red-400 hover:text-red-300 text-xs"
                      >
                        Remove
                      </button>
                    </div>
                  )}
                </div>
              ) : field.type === 'image' ? (
                <div className="space-y-2">
                  <input
                    type="text"
                    value={formData[field.name] || ''}
                    onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                    className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
                    placeholder="Enter image URL or upload below"
                  />
                  {formData[field.name] && (
                    <div className="mt-2">
                      <img 
                        src={formData[field.name]} 
                        alt="Preview" 
                        className="max-w-xs rounded border border-gray-600"
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                  )}
                  <p className="text-gray-500 text-xs">Paste an image URL or use the Media tab to upload and get a URL</p>
                </div>
              ) : field.type === 'textarea' ? (
                <textarea
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white h-28"
                />
              ) : (
                <input
                  type="text"
                  value={formData[field.name] || ''}
                  onChange={(e) => setFormData({ ...formData, [field.name]: e.target.value })}
                  className="w-full bg-gray-700 border border-gray-600 rounded px-4 py-2 text-white"
                />
              )}
            </div>
          ))}
          <button onClick={handleSave} className="bg-amber-600 hover:bg-amber-500 text-white px-6 py-2 rounded">
            Save Changes
          </button>
        </div>
      )}
    </div>
  );
};

// ============== MAIN ADMIN COMPONENT ==============
// ============== SITE SETTINGS TAB ==============
const SiteSettingsTab = ({ getAuthHeaders }) => {
  const [settings, setSettings] = useState({ pause_physical_memberships: false });
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [settingsRes, notifRes] = await Promise.all([
          axios.get(`${API}/admin/site-settings`, { headers: getAuthHeaders() }),
          axios.get(`${API}/admin/notify-availability`, { headers: getAuthHeaders() })
        ]);
        setSettings(settingsRes.data);
        setNotifications(notifRes.data);
      } catch (err) {
        console.error('Failed to fetch site settings:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/site-settings`, settings, { headers: getAuthHeaders() });
      setMessage({ type: 'success', text: 'Settings saved!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save settings' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  if (loading) return <div className="text-white p-8">Loading...</div>;

  return (
    <div className="max-w-3xl">
      <h2 className="text-2xl font-bold text-white mb-6">Site Settings</h2>

      {message && (
        <div className={`mb-4 p-3 rounded ${message.type === 'success' ? 'bg-green-600/20 text-green-400 border border-green-600/30' : 'bg-red-600/20 text-red-400 border border-red-600/30'}`}>
          {message.text}
        </div>
      )}

      {/* Membership Availability */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-4">Membership Availability</h3>
        
        <div className="flex items-center justify-between p-4 bg-gray-700/50 rounded-lg">
          <div>
            <p className="text-white font-medium">Pause Physical Prepaid Memberships</p>
            <p className="text-gray-400 text-sm mt-1">
              When enabled, physical prepaid (one-time) memberships will appear with an "unavailable" overlay. Recurring physical memberships remain active.
            </p>
          </div>
          <button
            onClick={() => setSettings(prev => ({ ...prev, pause_physical_memberships: !prev.pause_physical_memberships }))}
            data-testid="toggle-pause-physical"
            className={`relative w-12 h-6 rounded-full transition-colors flex-shrink-0 ml-4 ${settings.pause_physical_memberships ? 'bg-amber-500' : 'bg-gray-600'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full transition-transform ${settings.pause_physical_memberships ? 'translate-x-6' : ''}`} />
          </button>
        </div>

        <button
          onClick={handleSave}
          disabled={saving}
          data-testid="save-site-settings"
          className="mt-4 px-6 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded transition-colors disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {/* Availability Notifications */}
      {notifications.length > 0 && (
        <div className="bg-gray-800 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            Availability Notification Signups ({notifications.length})
          </h3>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {notifications.map((n, i) => (
              <div key={i} className="flex justify-between items-center p-3 bg-gray-700/50 rounded text-sm">
                <span className="text-white">{n.email}</span>
                <span className="text-gray-400">{n.created_at ? new Date(n.created_at).toLocaleDateString() : ''}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============== LEAD MAGNETS TAB ==============
// ============== IMAGE UPLOAD HELPER ==============
const ImageUploader = ({ label, image, onUpload, onRemove, onCaptionChange, onToggleVisibility, testIdPrefix, getAuthHeaders }) => {
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!['image/jpeg', 'image/png'].includes(file.type)) {
      alert('Only JPG and PNG files are supported.');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/admin/upload/lead-magnets`, formData, {
        headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
      });
      onUpload({ url: res.data.url, caption: '', visible: true, original_filename: file.name });
    } catch (err) {
      alert('Upload failed: ' + (err.response?.data?.detail || err.message));
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  return (
    <div className="bg-gray-700/40 rounded-lg p-4" data-testid={`${testIdPrefix}-section`}>
      <div className="flex items-center justify-between mb-3">
        <label className="text-gray-200 text-sm font-medium">{label}</label>
        {image?.url && (
          <button
            onClick={() => onToggleVisibility?.()}
            data-testid={`${testIdPrefix}-toggle-visibility`}
            className={`text-xs px-2 py-1 rounded ${image.visible !== false ? 'bg-green-600/20 text-green-400 border border-green-600/30' : 'bg-gray-600/40 text-gray-400 border border-gray-600/30'}`}
          >
            {image.visible !== false ? 'Visible' : 'Hidden'}
          </button>
        )}
      </div>
      {image?.url ? (
        <div className="space-y-2">
          <div className="relative group">
            <img
              src={`${BACKEND_URL || ''}${image.url}`}
              alt={image.caption || label}
              className="w-full max-h-48 object-cover rounded border border-gray-600"
              data-testid={`${testIdPrefix}-preview`}
            />
            <button
              onClick={onRemove}
              data-testid={`${testIdPrefix}-remove`}
              className="absolute top-2 right-2 bg-red-600/80 hover:bg-red-500 text-white w-7 h-7 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity text-sm"
            >
              &times;
            </button>
          </div>
          <input
            type="text"
            value={image.caption || ''}
            onChange={(e) => onCaptionChange?.(e.target.value)}
            placeholder="Add a caption (optional)"
            data-testid={`${testIdPrefix}-caption`}
            className="w-full bg-gray-700 text-white rounded px-3 py-1.5 text-xs border border-gray-600 focus:border-amber-500 focus:outline-none"
          />
        </div>
      ) : (
        <div>
          <input ref={fileInputRef} type="file" accept="image/jpeg,image/png" onChange={handleUpload} className="hidden" data-testid={`${testIdPrefix}-file-input`} />
          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid={`${testIdPrefix}-upload-btn`}
            className="w-full py-6 border-2 border-dashed border-gray-600 hover:border-amber-500/50 rounded-lg text-gray-400 hover:text-gray-300 transition-colors text-sm disabled:opacity-50"
          >
            {uploading ? 'Uploading...' : 'Click to upload image (JPG/PNG)'}
          </button>
        </div>
      )}
    </div>
  );
};

const LeadMagnetsTab = ({ getAuthHeaders }) => {
  const [config, setConfig] = useState({
    headline: 'Your Free Vintage Letter Writing Kit',
    description: '',
    download_url: '',
    email_subject: 'Your Free Letter Writing Kit from The Timeless Post',
    email_body: '',
    form_enabled: true,
    hero_image: null,
    gallery_images: [],
    secondary_image: null,
  });
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/admin/freebie/letter-kit`, { headers: getAuthHeaders() });
        if (res.data.config && Object.keys(res.data.config).length > 0) {
          setConfig(prev => ({ ...prev, ...res.data.config }));
        }
        setSubscribers(res.data.subscribers || []);
      } catch (err) {
        console.error('Failed to fetch freebie config:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [getAuthHeaders]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/freebie/letter-kit`, config, { headers: getAuthHeaders() });
      setMessage({ type: 'success', text: 'Saved!' });
    } catch (err) {
      setMessage({ type: 'error', text: 'Failed to save' });
    } finally {
      setSaving(false);
      setTimeout(() => setMessage(null), 3000);
    }
  };

  const exportCSV = () => {
    const csv = 'Name,Email,Date\n' + subscribers.map(s => `"${s.name || ''}","${s.email}","${s.subscribed_at || ''}"`).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = 'freebie-subscribers.csv'; a.click();
  };

  // Gallery helpers
  const addGalleryImage = (imgData) => {
    const newImg = { ...imgData, id: Date.now().toString(), featured: false, order: config.gallery_images.length };
    setConfig(prev => ({ ...prev, gallery_images: [...prev.gallery_images, newImg] }));
  };

  const removeGalleryImage = (idx) => {
    setConfig(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.filter((_, i) => i !== idx).map((img, i) => ({ ...img, order: i })),
    }));
  };

  const toggleGalleryFeatured = (idx) => {
    setConfig(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.map((img, i) => ({ ...img, featured: i === idx ? !img.featured : false })),
    }));
  };

  const toggleGalleryVisibility = (idx) => {
    setConfig(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.map((img, i) => i === idx ? { ...img, visible: img.visible === false ? true : false } : img),
    }));
  };

  const updateGalleryCaption = (idx, caption) => {
    setConfig(prev => ({
      ...prev,
      gallery_images: prev.gallery_images.map((img, i) => i === idx ? { ...img, caption } : img),
    }));
  };

  // Drag reorder
  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => {
    e.preventDefault();
    if (dragIdx === null || dragIdx === idx) return;
    setConfig(prev => {
      const imgs = [...prev.gallery_images];
      const [moved] = imgs.splice(dragIdx, 1);
      imgs.splice(idx, 0, moved);
      setDragIdx(idx);
      return { ...prev, gallery_images: imgs.map((img, i) => ({ ...img, order: i })) };
    });
  };
  const handleDragEnd = () => setDragIdx(null);

  // Gallery multi-upload
  const galleryInputRef = React.useRef(null);
  const [galleryUploading, setGalleryUploading] = useState(false);
  const handleGalleryUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const validFiles = files.filter(f => ['image/jpeg', 'image/png'].includes(f.type));
    if (validFiles.length !== files.length) alert('Some files were skipped (only JPG/PNG allowed).');
    setGalleryUploading(true);
    for (const file of validFiles) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(`${API}/admin/upload/lead-magnets`, formData, {
          headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' },
        });
        addGalleryImage({ url: res.data.url, caption: '', visible: true, original_filename: file.name });
      } catch (err) {
        console.error('Gallery upload failed:', err);
      }
    }
    setGalleryUploading(false);
    if (galleryInputRef.current) galleryInputRef.current.value = '';
  };

  if (loading) return <div className="text-white p-8">Loading...</div>;

  return (
    <div className="max-w-3xl">
      <h2 className="text-2xl font-bold text-white mb-6">Lead Magnets</h2>

      {message && (
        <div className={`mb-4 p-3 rounded ${message.type === 'success' ? 'bg-green-600/20 text-green-400 border border-green-600/30' : 'bg-red-600/20 text-red-400 border border-red-600/30'}`}>
          {message.text}
        </div>
      )}

      {/* Vintage Letter Writing Kit Config */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-4">Vintage Letter Writing Kit</h3>
        
        {/* Form toggle */}
        <div className="flex items-center justify-between p-3 bg-gray-700/50 rounded mb-4">
          <span className="text-white text-sm">Form Enabled</span>
          <button
            onClick={() => setConfig(prev => ({ ...prev, form_enabled: !prev.form_enabled }))}
            data-testid="toggle-freebie-form"
            className={`relative w-10 h-5 rounded-full transition-colors ${config.form_enabled ? 'bg-amber-500' : 'bg-gray-600'}`}
          >
            <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full transition-transform ${config.form_enabled ? 'translate-x-5' : ''}`} />
          </button>
        </div>

        <div className="space-y-4">
          <div>
            <label className="block text-gray-300 text-sm mb-1">Page Headline</label>
            <input type="text" value={config.headline} onChange={(e) => setConfig(prev => ({ ...prev, headline: e.target.value }))} className="w-full bg-gray-700 text-white rounded px-3 py-2 text-sm border border-gray-600 focus:border-amber-500 focus:outline-none" data-testid="freebie-headline-input" />
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-1">Page Description</label>
            <textarea value={config.description} onChange={(e) => setConfig(prev => ({ ...prev, description: e.target.value }))} rows={3} className="w-full bg-gray-700 text-white rounded px-3 py-2 text-sm border border-gray-600 focus:border-amber-500 focus:outline-none resize-none" data-testid="freebie-description-input" />
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-1">Download File URL</label>
            <input type="url" value={config.download_url} onChange={(e) => setConfig(prev => ({ ...prev, download_url: e.target.value }))} placeholder="https://example.com/letter-kit.pdf" className="w-full bg-gray-700 text-white rounded px-3 py-2 text-sm border border-gray-600 focus:border-amber-500 focus:outline-none" data-testid="freebie-download-url-input" />
            {!config.download_url && (
              <p className="text-red-400 text-xs mt-1 font-medium" data-testid="download-url-warning">Emails will NOT be sent until a Download URL is set.</p>
            )}
            {config.download_url && (
              <p className="text-green-400 text-xs mt-1">Download URL set — emails will be sent on form submission.</p>
            )}
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-1">Email Subject</label>
            <input type="text" value={config.email_subject} onChange={(e) => setConfig(prev => ({ ...prev, email_subject: e.target.value }))} className="w-full bg-gray-700 text-white rounded px-3 py-2 text-sm border border-gray-600 focus:border-amber-500 focus:outline-none" data-testid="freebie-email-subject-input" />
          </div>
          <div>
            <label className="block text-gray-300 text-sm mb-1">Email Body (HTML — use {'{{download_url}}'} and {'{{name}}'} as placeholders)</label>
            <textarea value={config.email_body} onChange={(e) => setConfig(prev => ({ ...prev, email_body: e.target.value }))} rows={6} className="w-full bg-gray-700 text-white rounded px-3 py-2 text-sm font-mono border border-gray-600 focus:border-amber-500 focus:outline-none resize-none" data-testid="freebie-email-body-input" />
          </div>
        </div>

        <button onClick={handleSave} disabled={saving} data-testid="save-freebie-config" className="mt-4 px-6 py-2 bg-amber-600 hover:bg-amber-500 text-white rounded transition-colors disabled:opacity-50">
          {saving ? 'Saving...' : 'Save Configuration'}
        </button>
      </div>

      {/* ========== IMAGE MANAGEMENT ========== */}
      <div className="bg-gray-800 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-white mb-2">Page Images</h3>
        <p className="text-gray-400 text-xs mb-5">Upload images for the landing page. Remember to Save Configuration above after making changes.</p>

        {/* Hero Image */}
        <div className="mb-5">
          <ImageUploader
            label="Hero Image (top of page)"
            image={config.hero_image}
            onUpload={(img) => setConfig(prev => ({ ...prev, hero_image: img }))}
            onRemove={() => setConfig(prev => ({ ...prev, hero_image: null }))}
            onCaptionChange={(caption) => setConfig(prev => ({ ...prev, hero_image: { ...prev.hero_image, caption } }))}
            onToggleVisibility={() => setConfig(prev => ({ ...prev, hero_image: { ...prev.hero_image, visible: prev.hero_image?.visible === false ? true : false } }))}
            testIdPrefix="hero-image"
            getAuthHeaders={getAuthHeaders}
          />
        </div>

        {/* Gallery Images */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <label className="text-gray-200 text-sm font-medium">Gallery Images (below "What's Included")</label>
            <span className="text-gray-500 text-xs">{config.gallery_images.length} image{config.gallery_images.length !== 1 ? 's' : ''}</span>
          </div>

          {config.gallery_images.length > 0 && (
            <div className="space-y-2 mb-3">
              {config.gallery_images.map((img, idx) => (
                <div
                  key={img.id || idx}
                  draggable
                  onDragStart={() => handleDragStart(idx)}
                  onDragOver={(e) => handleDragOver(e, idx)}
                  onDragEnd={handleDragEnd}
                  className={`flex items-center gap-3 bg-gray-700/40 rounded-lg p-3 cursor-grab ${dragIdx === idx ? 'ring-2 ring-amber-500/50' : ''}`}
                  data-testid={`gallery-image-${idx}`}
                >
                  <div className="text-gray-500 select-none cursor-grab" title="Drag to reorder">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M8 6h2v2H8V6zm6 0h2v2h-2V6zM8 11h2v2H8v-2zm6 0h2v2h-2v-2zm-6 5h2v2H8v-2zm6 0h2v2h-2v-2z"/></svg>
                  </div>
                  <img
                    src={`${BACKEND_URL || ''}${img.url}`}
                    alt={img.caption || `Gallery ${idx + 1}`}
                    className="w-16 h-12 object-cover rounded border border-gray-600"
                  />
                  <div className="flex-1 min-w-0">
                    <input
                      type="text"
                      value={img.caption || ''}
                      onChange={(e) => updateGalleryCaption(idx, e.target.value)}
                      placeholder="Caption (optional)"
                      className="w-full bg-gray-700 text-white rounded px-2 py-1 text-xs border border-gray-600 focus:border-amber-500 focus:outline-none"
                      data-testid={`gallery-caption-${idx}`}
                    />
                  </div>
                  <button
                    onClick={() => toggleGalleryFeatured(idx)}
                    title={img.featured ? 'Featured (priority)' : 'Set as featured'}
                    data-testid={`gallery-featured-${idx}`}
                    className={`text-xs px-2 py-1 rounded border whitespace-nowrap ${img.featured ? 'bg-amber-600/20 text-amber-400 border-amber-600/30' : 'bg-gray-600/30 text-gray-500 border-gray-600/30 hover:text-gray-300'}`}
                  >
                    {img.featured ? 'Featured' : 'Feature'}
                  </button>
                  <button
                    onClick={() => toggleGalleryVisibility(idx)}
                    data-testid={`gallery-visibility-${idx}`}
                    className={`text-xs px-2 py-1 rounded border ${img.visible !== false ? 'bg-green-600/20 text-green-400 border-green-600/30' : 'bg-gray-600/30 text-gray-500 border-gray-600/30'}`}
                  >
                    {img.visible !== false ? 'On' : 'Off'}
                  </button>
                  <button
                    onClick={() => removeGalleryImage(idx)}
                    data-testid={`gallery-remove-${idx}`}
                    className="text-red-400 hover:text-red-300 text-sm"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" /></svg>
                  </button>
                </div>
              ))}
            </div>
          )}

          <div>
            <input ref={galleryInputRef} type="file" accept="image/jpeg,image/png" multiple onChange={handleGalleryUpload} className="hidden" data-testid="gallery-file-input" />
            <button
              onClick={() => galleryInputRef.current?.click()}
              disabled={galleryUploading}
              data-testid="gallery-upload-btn"
              className="w-full py-4 border-2 border-dashed border-gray-600 hover:border-amber-500/50 rounded-lg text-gray-400 hover:text-gray-300 transition-colors text-sm disabled:opacity-50"
            >
              {galleryUploading ? 'Uploading...' : 'Add gallery images (JPG/PNG — select multiple)'}
            </button>
          </div>
          <p className="text-gray-500 text-xs mt-1">Drag to reorder. First featured image shows larger. Toggle visibility on/off per image.</p>
        </div>

        {/* Secondary Image */}
        <ImageUploader
          label="Secondary Image (between sections)"
          image={config.secondary_image}
          onUpload={(img) => setConfig(prev => ({ ...prev, secondary_image: img }))}
          onRemove={() => setConfig(prev => ({ ...prev, secondary_image: null }))}
          onCaptionChange={(caption) => setConfig(prev => ({ ...prev, secondary_image: { ...prev.secondary_image, caption } }))}
          onToggleVisibility={() => setConfig(prev => ({ ...prev, secondary_image: { ...prev.secondary_image, visible: prev.secondary_image?.visible === false ? true : false } }))}
          testIdPrefix="secondary-image"
          getAuthHeaders={getAuthHeaders}
        />

        <div className="mt-4 p-3 bg-amber-900/10 border border-amber-600/20 rounded text-amber-400 text-xs">
          Remember: After uploading/removing images, click "Save Configuration" above to persist your changes.
        </div>
      </div>

      {/* Subscribers */}
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-lg font-semibold text-white">Subscribers ({subscribers.length})</h3>
          {subscribers.length > 0 && (
            <button onClick={exportCSV} data-testid="export-subscribers-csv" className="px-4 py-1.5 bg-gray-700 hover:bg-gray-600 text-white text-sm rounded transition-colors">
              Export CSV
            </button>
          )}
        </div>
        {subscribers.length === 0 ? (
          <p className="text-gray-400 text-sm">No subscribers yet.</p>
        ) : (
          <div className="space-y-2 max-h-80 overflow-y-auto">
            {subscribers.map((s, i) => (
              <div key={i} className="flex justify-between items-center p-3 bg-gray-700/50 rounded text-sm">
                <div>
                  <span className="text-white">{s.name || 'Anonymous'}</span>
                  <span className="text-gray-400 ml-2">{s.email}</span>
                </div>
                <span className="text-gray-500 text-xs">{s.subscribed_at ? new Date(s.subscribed_at).toLocaleDateString() : ''}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const auth = useAdminAuth();
  const [activeTab, setActiveTab] = useState('dashboard');

  if (auth.loading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  if (!auth.isAuthenticated) {
    return <AdminLogin onLogin={auth} />;
  }

  const renderTab = () => {
    switch (activeTab) {
      case 'dashboard': return <DashboardTab getAuthHeaders={auth.getAuthHeaders} />;
      case 'homepage': return <HomepageBuilderTab getAuthHeaders={auth.getAuthHeaders} />;
      case 'hero-media': return <HeroMediaTab getAuthHeaders={auth.getAuthHeaders} />;
      case 'custom-pages': return <CustomPagesTab getAuthHeaders={auth.getAuthHeaders} />;
      case 'characters': return <CharactersTab getAuthHeaders={auth.getAuthHeaders} />;
      case 'letters': return <LettersTab getAuthHeaders={auth.getAuthHeaders} />;
      case 'products': return <ProductsTab getAuthHeaders={auth.getAuthHeaders} />;
      case 'memberships': return <MembershipsTab getAuthHeaders={auth.getAuthHeaders} />;
      case 'subscriptions': return <SubscriptionsTab getAuthHeaders={auth.getAuthHeaders} />;
      case 'orders': return <OrdersTab getAuthHeaders={auth.getAuthHeaders} />;
      case 'customers': return <CustomersTab getAuthHeaders={auth.getAuthHeaders} />;
      case 'emails': return <EmailsTab getAuthHeaders={auth.getAuthHeaders} />;
      case 'media': return <MediaTab getAuthHeaders={auth.getAuthHeaders} />;
      case 'pages': return <PagesTab getAuthHeaders={auth.getAuthHeaders} />;
      case 'site-settings': return <SiteSettingsTab getAuthHeaders={auth.getAuthHeaders} />;
      case 'lead-magnets': return <LeadMagnetsTab getAuthHeaders={auth.getAuthHeaders} />;
      default: return <DashboardTab getAuthHeaders={auth.getAuthHeaders} />;
    }
  };

  return (
    <div className="min-h-screen bg-gray-900 flex">
      <AdminSidebar activeTab={activeTab} setActiveTab={setActiveTab} onLogout={auth.logout} />
      <main className="flex-1 p-8 overflow-auto">
        {renderTab()}
      </main>
    </div>
  );
};

export default AdminDashboard;
